import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Stack,
  Switch,
  Tooltip,
  Typography,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FunctionsIcon from "@mui/icons-material/Functions";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import GridViewIcon from "@mui/icons-material/GridView";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LinkIcon from "@mui/icons-material/Link";
import ShortTextIcon from "@mui/icons-material/ShortText";
import CreateIcon from "@mui/icons-material/Create";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import QuizIcon from "@mui/icons-material/Quiz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClassIcon from "@mui/icons-material/Class";
import api from "../../api";
import BookViewerDialog from "../../components/BookViewerDialog";
import QuestionImageUploader from "../../components/QuestionImageUploader";
import VideoExplanationPlayer from "../../components/VideoExplanationPlayer";
import { PreviewLatex } from "../../components/PreviewLatex";
import PassageWithLineNumbers from "../QuestionBank/components/PassageWithLineNumbers";
import { computePassageWrapWidth } from "../QuestionBank/utils/passageLineExtract";
import { parseBlanks } from "../../utils/parseBlanks";
import { toast } from "react-toastify";
import { QUILL_MODULES, QUILL_FORMATS, QuillR2Editor } from "../../utils/quillQuestionEditorConfig";

/**
 * Poll AI verification batch until completed or failed (async queue + OpenAI).
 * Backs off as the wait grows, and reports a timeout as a status rather than an error —
 * a slow batch is still running, which is a different situation from a failed one.
 */
async function pollAiVerifyBatch(batchId, { maxWaitMs = 300000 } = {}) {
  const startedAt = Date.now();
  let attempt = 0;

  while (Date.now() - startedAt < maxWaitMs) {
    const res = await api.get(`admin/ai/verify-batch/${batchId}`);
    const data = res?.data?.data;
    if (data?.status === "completed" || data?.status === "failed") {
      return data;
    }
    attempt += 1;
    const delay = attempt <= 5 ? 1500 : attempt <= 15 ? 3000 : 5000;
    await new Promise((r) => setTimeout(r, delay));
  }

  return { status: "poll_timeout" };
}

function applyVerificationToQuestions(questions, batchData) {
  const per = batchData?.verification_report?.per_question || {};
  return questions.map((q) => {
    const ref = q.questionId;
    if (ref && per[ref]) {
      return { ...q, _aiVerification: per[ref] };
    }
    return q;
  });
}

const verdictChipColor = (status) => {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s === "correct" || s === "passed" || s === "ok" || s === "valid") return "success";
  if (s === "incorrect" || s === "wrong" || s === "invalid" || s === "issues_found") return "error";
  if (s === "not_applicable" || s === "na" || s === "n/a") return "default";
  return "warning";
};

const stripEmptyQuill = (html) => {
  const stripped = (html || "").replace(/<p><br\s*\/?><\/p>/gi, "").trim();
  return stripped === "" ? "" : (html || "");
};

function VerdictCard({ label, verdict, visualSrc }) {
  const status = verdict?.status || "";
  const rationale = verdict?.rationale || "";
  const chipColor = verdictChipColor(status);
  const isNA = status.toLowerCase() === "not_applicable" || status === "na" || status === "n/a";

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: chipColor === "error" ? "error.light" : chipColor === "success" ? "success.light" : "divider",
        bgcolor: chipColor === "error" ? "rgba(244,67,54,0.04)" : chipColor === "success" ? "rgba(46,125,50,0.04)" : "background.paper",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: rationale ? 0.75 : 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0 }}>
          {label}
        </Typography>
        {status && (
          <Chip
            size="small"
            color={chipColor}
            variant={chipColor === "default" ? "outlined" : "filled"}
            label={status.replace(/_/g, " ")}
            sx={{ fontSize: 10, height: 18 }}
          />
        )}
      </Stack>
      {visualSrc && (
        <Box sx={{ mb: 0.75, maxWidth: 160, borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
          <img src={visualSrc} alt="Question visual" style={{ width: "100%", height: "auto", display: "block" }} />
        </Box>
      )}
      {rationale && !isNA && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.5 }}>
          {rationale}
        </Typography>
      )}
      {isNA && (
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontStyle: "italic" }}>
          Not applicable for this question type.
        </Typography>
      )}
    </Box>
  );
}

function QuestionVerificationRow({ q, batchId, onBatchUpdated, onQuestionFixed, busy, setBusy, onError }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const v = q._aiVerification;
  if (!batchId || !v) return null;

  const status = v.effective_overall_status || v.overall_status;
  const unresolvedIssues = (v.issues || []).filter((iss) => !iss?.resolved && !iss?.dismissed && !iss?.verified_by_admin);
  const color = status === "passed" ? "success" : status === "issues_found" ? "error" : "warning";
  const label = status === "passed"
    ? (v.accepted_by_admin ? "Accepted" : "Verified")
    : status === "issues_found"
      ? "Issues found"
      : "Needs review";

  const visualSrc = q.visualImageUrl || q?.visual?.url || q?.visual?.r2_url || q?.visual?.image_url || q?.visual?.src || null;

  const rowBusy = busy === q.questionId;

  // Every mutation endpoint returns the updated batch, so the response is applied directly
  // instead of re-fetching the whole batch after each click.
  const runAction = async (action, failureMessage) => {
    setBusy?.(q.questionId);
    try {
      const res = await action();
      const batch = res?.data?.data;
      if (batch) onBatchUpdated(batch);
      return batch;
    } catch (e) {
      onError?.(e?.response?.data?.message || e?.message || failureMessage);
      return null;
    } finally {
      setBusy?.(null);
    }
  };

  const handleAiFix = async () => {
    setBusy?.(q.questionId);
    try {
      const res = await api.post(`admin/ai/verify-batch/${batchId}/questions/${encodeURIComponent(q.questionId)}/fix`);
      const data = res?.data?.data;
      if (data?.fixed_question) {
        const fq = data.fixed_question;
        onQuestionFixed?.(q.questionId, {
          ...fq,
          visualImageUrl: fq.visualImageUrl ?? fq.visual_image_url ?? q.visualImageUrl,
        });
      }
      if (data?.batch) onBatchUpdated(data.batch);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "AI Fix failed for this question.";
      onError?.(msg);
    } finally {
      setBusy?.(null);
    }
  };

  const handleRegenerateVisual = async () => {
    setBusy?.(q.questionId);
    try {
      const res = await api.post("admin/ai/regenerate-visual", {
        visual: q.visual || null,
        question: q.question || "",
        subject: q.subject || "",
        strategy: "respec",
      });
      const data = res?.data?.data;
      if (!data?.visual_image_url && !data?.visual) {
        onError?.("Could not regenerate a visual for this question.");
        return;
      }
      onQuestionFixed?.(q.questionId, { visual: data.visual, visualImageUrl: data.visual_image_url });
      const retry = await api.post(`admin/ai/verify-batch/${batchId}/questions/${encodeURIComponent(q.questionId)}/retry`);
      const batch = retry?.data?.data;
      if (batch) onBatchUpdated(batch);
      toast.success(data.respecced
        ? "Visual re-described by AI, redrawn and re-verified."
        : "Visual regenerated and re-verified.");
    } catch (e) {
      onError?.(e?.response?.data?.message || e?.message || "Regenerating the visual failed.");
    } finally {
      setBusy?.(null);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: color === "error" ? "error.light" : color === "success" ? "success.light" : "warning.light",
        overflow: "hidden",
      }}
    >
      {/* Header bar */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: color === "error" ? "rgba(244,67,54,0.06)" : color === "success" ? "rgba(46,125,50,0.06)" : "rgba(255,152,0,0.08)",
          borderBottom: detailOpen ? "1px solid" : "none",
          borderColor: "divider",
          cursor: "pointer",
        }}
        onClick={() => setDetailOpen((p) => !p)}
      >
        <Chip size="small" color={color} label={label} sx={{ fontWeight: 700 }} />
        <Chip
          size="small"
          color={verdictChipColor(v.answer_verdict?.status)}
          variant="outlined"
          label={`Answer: ${v.answer_verdict?.status || "—"}`}
          sx={{ fontSize: 11 }}
        />
        <Chip
          size="small"
          color={verdictChipColor(v.visual_verdict?.status)}
          variant="outlined"
          label={`Visual: ${v.visual_verdict?.status || "—"}`}
          sx={{ fontSize: 11 }}
        />
        <Chip
          size="small"
          color={verdictChipColor(v.question_text_verdict?.status)}
          variant="outlined"
          label={`Stem: ${v.question_text_verdict?.status || "—"}`}
          sx={{ fontSize: 11 }}
        />
        {unresolvedIssues.length > 0 && (
          <Chip size="small" color="error" label={`${unresolvedIssues.length} issue${unresolvedIssues.length > 1 ? "s" : ""}`} sx={{ fontSize: 11 }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ userSelect: "none" }}>
          {detailOpen ? "▲ Hide details" : "▼ Show details"}
        </Typography>
      </Stack>

      {detailOpen && (
        <Box sx={{ p: 1.5 }}>
          {/* Verdict cards */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <VerdictCard label="Answer" verdict={v.answer_verdict} />
            <VerdictCard label="Visual" verdict={v.visual_verdict} visualSrc={visualSrc} />
            <VerdictCard label="Stem" verdict={v.question_text_verdict} />
          </Stack>

          {/* Unresolved issues */}
          {unresolvedIssues.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              {unresolvedIssues.map((iss) => (
                <Alert
                  key={iss.composite_key || `${iss.key}-${iss.message}`}
                  severity={iss.severity === "high" ? "error" : iss.severity === "medium" ? "warning" : "info"}
                  sx={{ mb: 1, py: 0.5 }}
                >
                  {iss.key && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={String(iss.key).replace(/_/g, " ")}
                      sx={{ fontSize: 10, height: 18, mb: 0.75 }}
                    />
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>{iss.message}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      disabled={rowBusy}
                      onClick={() => runAction(
                        () => api.post(`admin/ai/verify-batch/${batchId}/dismiss-issue`, {
                          issue_key: iss.composite_key || `${q.questionId}::${iss.key}`,
                        }),
                        "Dismissing the issue failed."
                      )}
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="small"
                      disabled={rowBusy}
                      onClick={() => runAction(
                        () => api.post(`admin/ai/verify-batch/${batchId}/verify-issue`, {
                          issue_key: iss.composite_key || `${q.questionId}::${iss.key}`,
                        }),
                        "Marking the issue OK failed."
                      )}
                    >
                      Mark issue OK
                    </Button>
                  </Stack>
                </Alert>
              ))}
            </Box>
          )}

          {/* Action buttons */}
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            {status !== "passed" && (
              <Button size="small" variant="contained" color="warning" disabled={rowBusy} onClick={handleAiFix}>
                AI Fix
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={rowBusy}
              onClick={() => runAction(
                () => api.post(`admin/ai/verify-batch/${batchId}/questions/${encodeURIComponent(q.questionId)}/retry`),
                "Re-verification failed."
              )}
            >
              Re-verify
            </Button>
            {(q.visual || q.visualImageUrl) && (
              <Button size="small" variant="outlined" disabled={rowBusy} onClick={handleRegenerateVisual}>
                Regenerate visual
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={rowBusy}
              onClick={async () => {
                const batch = await runAction(
                  () => api.post(`admin/ai/verify-batch/${batchId}/accept-question`, { question_ref: q.questionId }),
                  "Accepting the question failed."
                );
                if (batch) toast.success("Question accepted. Click Save to database to persist.");
              }}
            >
              Accept question
            </Button>
            {rowBusy && <CircularProgress size={16} />}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Constants ────────────────────────────────────────────────────────────────

const STEPS = ["Subject & Topic", "Configure", "AI Generation", "Preview & Download"];

const normSchoolYear = (s) => String(s || "").trim().toLowerCase();

const courseNameOf = (c) => {
  const raw = c?.course_name || c?.name || "";
  return String(raw).replace(/\s*[-–]\s*\d{4}\/\d{4}\s*$/, "").trim() || String(raw).trim();
};

const extractApiErrorMessage = (e, fallback = "Request failed") => {
  const data = e?.response?.data;
  const status = e?.response?.status;

  if (data?.code === "ai_quota_exceeded" || status === 402) {
    return data?.message || "AI credits are exhausted — please recharge your OpenAI billing and try again.";
  }
  if (data?.code === "ai_rate_limited" || status === 429) {
    return data?.message || "AI provider rate limit reached — wait a moment and try again.";
  }
  if (data?.message && typeof data.message === "string" && data.message !== "Server Error") {
    return data.message;
  }
  const errors = data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat()[0];
    if (first) return String(first);
  }
  // No response at all: request never completed (offline, proxy timeout, request aborted).
  if (!e?.response) {
    if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
      return "The AI request took too long and timed out before the server replied. Try fewer questions or generate one type at a time.";
    }
    return "Could not reach the server (no response). This is usually a dropped connection or a server timeout — check your internet connection and try again.";
  }
  if (status >= 500) {
    return `The server failed while handling this request (HTTP ${status}). Check the backend logs for the exact error.`;
  }
  if (e?.message) return e.message;
  return fallback;
};

const prepareQuestionsForSave = (questions) =>
  (questions || []).map((q) => ({
    question: q.question || "",
    questionId: q.questionId,
    questionType: q.questionType || q._formatLabel,
    options: Array.isArray(q.options) ? q.options.map((o) => String(o)) : q.options,
    definitions: Array.isArray(q.definitions) ? q.definitions.map((d) => String(d)) : q.definitions,
    answer: q.answer != null ? String(q.answer) : q.answer,
    strand: q.strand,
    subSubject: q.subSubject,
    visual: q.visual,
    explanation: q.explanation,
    writing_meta: q.writing_meta,
    passage: q.passage || q.passage_text || null,
    audio_url: q.audio_url || q._audioUrl || null,
    sub_questions: q.sub_questions || null,
    _formatKey: q._formatKey,
    _aiVerification: q._aiVerification,
  }));

const SUBJECT_CONFIG = {
  maths: { Icon: FunctionsIcon, color: "#1565c0", bg: "#e3f2fd" },
  english: { Icon: MenuBookIcon, color: "#2e7d32", bg: "#e8f5e9" },
  verbal_reasoning: { Icon: RecordVoiceOverIcon, color: "#6a1b9a", bg: "#f3e5f5" },
  non_verbal_reasoning: { Icon: GridViewIcon, color: "#bf360c", bg: "#fbe9e7" },
};

const ENGLISH_FORMATS = [
  { key: "multiple_choice", label: "Multiple-choice", Icon: CheckCircleOutlineIcon, defaultCount: 10, hasCount: true },
  { key: "fill_blanks", label: "Fill in the Blanks", Icon: EditIcon, defaultCount: 10, hasCount: true },
  {
    key: "vocab_matching",
    label: "Vocabulary Matching",
    Icon: LinkIcon,
    defaultCount: 10,
    hasCount: true,
    hasNativeLanguage: true,
  },
  { key: "short_answer", label: "Short-answer", Icon: ShortTextIcon, defaultCount: 4, hasCount: true },
  {
    key: "writing_composition",
    label: "Writing Composition",
    Icon: CreateIcon,
    defaultCount: 1,
    hasCount: true,
    countLabel: "Prompts",
  },
  {
    key: "reading_comprehension",
    label: "Reading Comprehension",
    Icon: MenuBookIcon,
    defaultCount: 1,
    hasCount: true,
    countLabel: "Passages",
    marksLabel: "10 marks",
  },
];

// These two types are universal — available across all 4 subjects
const UNIVERSAL_FORMAT_LABELS = ["Multiple-choice", "Short-answer"];

// ── Per-subject marks & duration config (mirrors backend resolveMarksAndDuration) ─────
// Used to show live marks hints in the UI and compute running total.
const SUBJECT_TYPE_CONFIG = {
  english: {
    multiple_choice:     { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    fill_blanks:         { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    vocab_matching:      { marksPerQ: null, duration: 120, marksLabel: "1 mark per pair" },
    short_answer:        { marksPerQ: 5, duration: 60,  marksLabel: "5 marks each" },
    writing_composition: { marksPerQ: 20, duration: 600, marksLabel: "20 marks (75–100 words)" },
    reading_comprehension: { marksPerQ: 10, duration: 180, marksLabel: "10 marks" },
  },
  maths: {
    multiple_choice:      { marksPerQ: 3, duration: 45,  marksLabel: "3 marks each" },
    short_answer:         { marksPerQ: 5, duration: 60,  marksLabel: "5 marks each" },
    diagram_interpretation: { marksPerQ: 4, duration: 90, marksLabel: "4 marks each" },
  },
  verbal_reasoning: {
    multiple_choice:       { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    short_answer:          { marksPerQ: 3, duration: 45,  marksLabel: "3 marks each" },
    synonym_antonym_sets:  { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    code_breaking:         { marksPerQ: 3, duration: 60,  marksLabel: "3 marks each" },
    series_completion:     { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    analogy_mcqs:          { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
  },
  non_verbal_reasoning: {
    multiple_choice:   { marksPerQ: 2, duration: 30,  marksLabel: "2 marks each" },
    short_answer:      { marksPerQ: 3, duration: 45,  marksLabel: "3 marks each" },
    figure_analogies:  { marksPerQ: 2, duration: 45,  marksLabel: "2 marks each" },
    shape_sequences:   { marksPerQ: 3, duration: 60,  marksLabel: "3 marks each" },
    matrix_completion: { marksPerQ: 2, duration: 45,  marksLabel: "2 marks each" },
    code_grids:        { marksPerQ: 2, duration: 60,  marksLabel: "2 marks each" },
  },
};

/** Get marks config for a given subject key + format key */
const getTypeConfig = (subjectKey, formatKey) => {
  const subConf = SUBJECT_TYPE_CONFIG[subjectKey] || {};
  return subConf[formatKey] || { marksPerQ: 1, duration: 30, marksLabel: "1 mark each" };
};

/**
 * Compute total marks for the current selection.
 * Vocab matching: 1 mark per pair (count = number of pairs).
 * Writing: flat marks regardless of count (always 1 question).
 */
const MAX_QUESTIONS_PER_TEST = 60;

// Effective marks-per-question for a type, preferring the stored override over the config default.
const effectiveMpq = (subjectKey, fmtKey, selectedFormats) => {
  if (fmtKey === "vocab_matching") return 1; // always 1 mark per pair
  const override = selectedFormats[fmtKey]?.marksPerQ;
  if (override != null) return override;
  return getTypeConfig(subjectKey, fmtKey).marksPerQ ?? 1;
};

const computeTotalMarks = (subjectKey, selectedFormats, currentFormatCards) => {
  let total = 0;
  currentFormatCards.forEach((fmt) => {
    if (!selectedFormats[fmt.key]?.selected) return;
    const count = selectedFormats[fmt.key]?.count ?? fmt.defaultCount ?? 1;
    if (fmt.key === "reading_comprehension") {
      // RC is a passage reference — marks come from the other question types
    } else if (fmt.key === "vocab_matching") {
      total += count; // 1 mark per pair
    } else if (fmt.key === "writing_composition") {
      total += effectiveMpq(subjectKey, fmt.key, selectedFormats);
    } else {
      total += effectiveMpq(subjectKey, fmt.key, selectedFormats) * count;
    }
  });
  return total;
};

// Total questions across all selected types (vocab = pairs count, writing = prompts count)
const computeTotalQuestions = (selectedFormats, currentFormatCards) => {
  let total = 0;
  currentFormatCards.forEach((fmt) => {
    if (!selectedFormats[fmt.key]?.selected || !fmt.hasCount) return;
    if (fmt.key === "reading_comprehension") return;
    total += selectedFormats[fmt.key]?.count ?? fmt.defaultCount ?? 1;
  });
  return total;
};

/**
 * Redistribute marks across adjustable types to reach exactly 100 marks.
 * Fixed types (vocab_matching at 1/pair, writing_composition at config mpq) keep their marks.
 * Adjustable types start at their config default mpq, then are increased/decreased to hit target.
 */
const redistributeToTarget = (subjectKey, formats, formatCards, target = 100) => {
  const result = {};
  Object.keys(formats).forEach((k) => { result[k] = { ...formats[k] }; });

  let fixedMarks = 0;
  const adjustable = [];

  formatCards.forEach((fmt) => {
    const sel = result[fmt.key];
    if (!sel?.selected) return;
    const count = sel.count ?? fmt.defaultCount ?? 1;
    if (fmt.key === "vocab_matching") {
      fixedMarks += count; // 1 mark per pair
    } else if (fmt.key === "reading_comprehension") {
      // RC is a passage reference — not counted in marks
    } else if (fmt.key === "writing_composition") {
      fixedMarks += getTypeConfig(subjectKey, fmt.key).marksPerQ ?? 20;
    } else {
      adjustable.push({
        key: fmt.key,
        count,
        defaultMpq: getTypeConfig(subjectKey, fmt.key).marksPerQ ?? 1,
        mpq: getTypeConfig(subjectKey, fmt.key).marksPerQ ?? 1,
      });
    }
  });

  if (adjustable.length === 0) return result;

  const budget = Math.max(0, target - fixedMarks);

  // If budget is zero or negative, set all adjustable to minimum 1
  if (budget <= 0) {
    adjustable.forEach((t) => { result[t.key] = { ...result[t.key], marksPerQ: 1 }; });
    return result;
  }

  // Start all at default mpq
  let currentTotal = adjustable.reduce((s, t) => s + t.mpq * t.count, 0);

  if (currentTotal < budget) {
    // Need more marks — greedily increase mpq, prefer largest count that fits the gap
    let gap = budget - currentTotal;
    let changed = true;
    while (gap > 0 && changed) {
      changed = false;
      // Pick the type with the largest count that still fits in the remaining gap
      let best = null;
      for (const t of adjustable) {
        if (t.count <= gap && (best === null || t.count > best.count)) best = t;
      }
      if (best) {
        best.mpq += 1;
        gap -= best.count;
        changed = true;
      }
    }
  } else if (currentTotal > budget) {
    // Need fewer marks — greedily decrease mpq, prefer largest count (saves most per step)
    let excess = currentTotal - budget;
    let changed = true;
    while (excess > 0 && changed) {
      changed = false;
      let best = null;
      for (const t of adjustable) {
        if (t.mpq > 1 && t.count <= excess && (best === null || t.count > best.count)) best = t;
      }
      if (best) {
        best.mpq -= 1;
        excess -= best.count;
        changed = true;
      }
    }
  }

  adjustable.forEach((t) => { result[t.key] = { ...result[t.key], marksPerQ: t.mpq }; });
  return result;
};

const NATIVE_LANGUAGES = [
  "English", "Spanish", "French", "German", "Arabic",
  "Mandarin Chinese", "Hindi", "Portuguese", "Russian",
  "Japanese", "Korean", "Italian",
];

// Curated UK 11+ creative writing prompts (age 10-11, Year 6)
const WRITING_COMPOSITION_PROMPTS = [
  // Narrative / Story
  "You discover a locked door in your school that has never been there before. Write a story about what happens when you open it.",
  "You wake up one morning to find you can speak to animals. Describe your day and what you learn.",
  "A mysterious package arrives at your front door with your name on it but no return address. Write the story of what happens next.",
  "You find a map hidden inside an old library book. Write about the adventure it leads you on.",
  "You are the last person on Earth to have not yet been to space. Write about the day you finally go.",
  "Write a story about a friendship between two very different people that changes both of their lives.",
  "Your shadow starts behaving strangely — it moves on its own. Write a story about what happens.",
  "You travel back in time to an important moment in history. Describe what you see, feel and do.",
  "Write a story that begins: 'Nobody believed me when I said the old lighthouse was haunted.'",
  "A small robot arrives at your door claiming to be from the future. What does it want, and what do you do?",
  // Descriptive
  "Describe a place you love visiting. Use your senses to paint a vivid picture for the reader.",
  "Describe the busiest market you can imagine — the sights, sounds, smells and the people there.",
  "Write a description of a storm seen from inside a cosy house. Make the reader feel both the danger outside and the warmth inside.",
  "Describe an abandoned building. What does it look like? What stories might it hold?",
  "Write a description of an underwater world as though you are swimming through it for the first time.",
  // Persuasive / Opinion
  "Write a persuasive letter to your headteacher arguing that school should start one hour later every day.",
  "Write a speech to convince your class that your town or city deserves a brand-new park.",
  "Should children be allowed to use social media? Write your opinion clearly, giving reasons and examples.",
  // Imaginative / Creative
  "If you could invent a new subject to be taught at school, what would it be? Write about it and why it matters.",
  "Imagine you are an explorer who has just discovered a land that no one else knows about. Write about your discovery.",
  "Write about a day when everything went wrong — but turned out better than expected.",
  "You are given the chance to redesign your school from scratch. Describe what it would be like.",
  "Write a story from the perspective of an object that is thrown away and forgotten.",
  "Describe a world where music is banned. What would life be like, and how would people react?",
];

const pickRandomWritingPrompt = (exclude = "") => {
  const available = WRITING_COMPOSITION_PROMPTS.filter((p) => p !== exclude);
  return available[Math.floor(Math.random() * available.length)];
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const slugify = (value = "") =>
  value.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "ai-test";

const resolveVisualAsset = (visualImageUrl, visual) => {
  const urlCandidate = [
    visualImageUrl,
    visual?.url,
    visual?.r2_url,
    visual?.public_url,
    visual?.image_url,
    visual?.src,
  ].find((v) => typeof v === "string" && v.trim().length > 0);
  if (urlCandidate) return { type: "image", src: urlCandidate, alt: visual?.alt };
  if (visual?.svg) return { type: "svg", svg: visual.svg, alt: visual?.alt };
  return null;
};

const getFormatIcon = (label = "") => {
  const l = label.toLowerCase();
  if (l.includes("multiple") || l.includes("mcq") || l.includes("analogy")) return CheckCircleOutlineIcon;
  if (l.includes("short")) return ShortTextIcon;
  if (l.includes("diagram") || l.includes("figure")) return VisibilityIcon;
  if (l.includes("shape") || l.includes("matrix") || l.includes("grid") || l.includes("code")) return GridViewIcon;
  if (l.includes("series") || l.includes("sequence")) return AutoAwesomeIcon;
  return QuizIcon;
};

const buildNonEnglishFormats = (questionFormats = [], subjectKey = "") => {
  const toKey = (label) =>
    label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  const getDefaultCount = (key, label) => {
    // Use plan-specified default counts per subject type config
    const typeDefaults = {
      // Maths
      multiple_choice: 10, short_answer: 10, diagram_interpretation: 5,
      // VR
      synonym_antonym_sets: 10, code_breaking: 5, series_completion: 10, analogy_mcqs: 10,
      // NVR
      figure_analogies: 10, shape_sequences: 5, matrix_completion: 10, code_grids: 10,
    };
    return typeDefaults[key] ?? (label.toLowerCase().includes("short") ? 5 : 5);
  };

  const existing = questionFormats.map((label) => {
    const key = toKey(label);
    return {
      key,
      label,
      Icon: getFormatIcon(label),
      defaultCount: getDefaultCount(key, label),
      hasCount: true,
    };
  });

  // Prepend universal types if not already supplied by the subject config
  const existingLabelsLower = existing.map((f) => f.label.toLowerCase());
  const universalExtras = UNIVERSAL_FORMAT_LABELS
    .filter((ul) => !existingLabelsLower.includes(ul.toLowerCase()))
    .map((label) => {
      const key = toKey(label);
      return {
        key,
        label,
        Icon: getFormatIcon(label),
        defaultCount: getDefaultCount(key, label),
        hasCount: true,
      };
    });

  return [...universalExtras, ...existing];
};

// ── Preview sub-components ────────────────────────────────────────────────────

function QuestionVisualImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 120 }}>
      {!loaded && !error && (
        <Skeleton variant="rectangular" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", maxHeight: 260 }} />
      )}
      <img
        alt={alt}
        src={src}
        style={{ width: "100%", height: "auto", display: loaded ? "block" : "none" }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {error && <Typography variant="body2" color="text.secondary">Failed to load visual</Typography>}
    </Box>
  );
}

function VocabMatchingPreview({
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setBusy,
  onError,
}) {
  // Vocab matching: 1 question object with options[] = words, definitions[] = definitions
  const q = questions[0];
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    question: q?.question || "",
    wordsText: Array.isArray(q?.options) ? q.options.join("\n") : "",
    defsText: Array.isArray(q?.definitions) ? q.definitions.join("\n") : "",
  });
  const { words, shuffledDefs, instruction } = useMemo(() => {
    if (!q) return { words: [], shuffledDefs: [], instruction: "" };
    const rawOpts = q.options || [];
    const defs = q.definitions || [];

    // Sanitize: each entry should be a plain string. If an entry is an object
    // (e.g. { name, id } from a DB-backed question) extract the label.
    // Also guard against a garbled entry that encodes multiple words in one
    // CSV/JSON string (artifact of maxItems schema mismatch with OpenAI).
    const sanitize = (v) => (typeof v === "string" ? v : (v?.name ?? v?.text ?? String(v)));
    const opts = rawOpts.flatMap((entry) => {
      const s = sanitize(entry).trim();
      // If a single entry looks like it contains multiple quoted words (e.g.
      // `adjacent","ample","brisk"`) split it out so the pairs display correctly.
      if (s.includes('","')) {
        return s
          .split('","')
          .map((w) => w.replace(/^"|"$/g, "").trim())
          .filter(Boolean);
      }
      return s ? [s] : [];
    });

    const w = opts.map((word, i) => ({ num: i + 1, text: word || "—" }));
    const d = defs.map((def, i) => ({
      letter: String.fromCharCode(65 + i),
      text: def || "—",
    }));
    const shuffled = [...d];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return { words: w, shuffledDefs: shuffled, instruction: q.question || "" };
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!q) return <Typography color="text.secondary">No vocabulary matching content generated.</Typography>;

  const isCompact = words.length > 6;

  return (
    <Box>
      <QuestionVerificationRow
        q={q}
        batchId={verifyBatchId}
        onBatchUpdated={onVerificationBatchUpdated}
        onQuestionFixed={onQuestionFixed}
        busy={verificationBusy}
        setBusy={setBusy}
      />
      {instruction && (
        <Box sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}>
          <PreviewLatex html={instruction} />
        </Box>
      )}
      <Typography variant="caption" sx={{ mb: 1, display: "block", color: "text.secondary" }}>
        {words.length} word–definition pairs
      </Typography>
      <Box sx={words.length > 8 ? { maxHeight: 480, overflowY: "auto", pr: 1 } : {}}>
        <Grid container spacing={isCompact ? 1 : 2}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}
            >
              Words
            </Typography>
            {words.map(({ num, text }) => (
              <Box key={num} sx={{ mb: isCompact ? 0.5 : 1, p: isCompact ? 1 : 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, fontSize: isCompact ? "0.85rem" : undefined }}>
                  <Typography component="span" variant="body2" sx={isCompact ? { fontSize: "0.85rem" } : {}}>{num}.</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PreviewLatex html={text} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}
            >
              Definitions
            </Typography>
            {shuffledDefs.map(({ letter, text }) => (
              <Box key={letter} sx={{ mb: isCompact ? 0.5 : 1, p: isCompact ? 1 : 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, fontSize: isCompact ? "0.85rem" : undefined }}>
                  <Typography component="span" variant="body2" sx={isCompact ? { fontSize: "0.85rem" } : {}}>{letter}.</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PreviewLatex html={text} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setDraft({
              question: q.question || "",
              wordsText: Array.isArray(q.options) ? q.options.join("\n") : "",
              defsText: Array.isArray(q.definitions) ? q.definitions.join("\n") : "",
            });
            setEditing(true);
          }}
        >
          Edit question
        </Button>
        {!q.dbId && <Typography variant="caption" color="text.secondary">Unsaved question: edits remain in preview only.</Typography>}
      </Stack>
      {editing && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
          <TextField
            label="Instruction"
            fullWidth
            multiline
            minRows={2}
            value={draft.question}
            onChange={(e) => setDraft((p) => ({ ...p, question: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Words (one per line)"
            fullWidth
            multiline
            minRows={4}
            value={draft.wordsText}
            onChange={(e) => setDraft((p) => ({ ...p, wordsText: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Definitions (one per line)"
            fullWidth
            multiline
            minRows={4}
            value={draft.defsText}
            onChange={(e) => setDraft((p) => ({ ...p, defsText: e.target.value }))}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={saving}
              onClick={async () => {
                const options = draft.wordsText.split("\n").map((v) => v.trim()).filter(Boolean);
                const definitions = draft.defsText.split("\n").map((v) => v.trim()).filter(Boolean);
                if (!draft.question.trim() || options.length === 0) {
                  onError?.("Instruction and at least one word are required.");
                  return;
                }

                setQuestions((prev) => prev.map((item) => (
                  item.questionId === q.questionId
                    ? { ...item, question: draft.question.trim(), options, definitions }
                    : item
                )));

                if (!q.dbId) {
                  setEditing(false);
                  return;
                }

                setSaving(true);
                try {
                  await api.put(`admin/ai/question/${q.dbId}/content`, {
                    question: draft.question.trim(),
                    options,
                    definitions,
                    answer: null,
                    explanation: q.explanation || null,
                    visual: q.visual || null,
                  });
                  setEditing(false);
                } catch (e) {
                  const msg = e?.response?.data?.message || e?.message || "Failed to save vocabulary edits.";
                  onError?.(msg);
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? "Saving..." : (q.dbId ? "Save to database" : "Apply locally")}
            </Button>
            <Button size="small" variant="outlined" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function WritingCompositionPreview({ questions, setQuestions, verifyBatchId, onVerificationBatchUpdated, onQuestionFixed, verificationBusy, setBusy }) {
  const [saving, setSaving] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState(null); // null | "success" | "error"

  const q = questions[0];
  if (!q) return <Typography color="text.secondary">No composition topic generated.</Typography>;

  const wm = q.writing_meta || {};
  const update = (field, value) =>
    setQuestions((prev) =>
      prev.map((item) =>
        item === q ? { ...item, writing_meta: { ...item.writing_meta, [field]: value } } : item
      )
    );

  const handleSave = async () => {
    if (!q.dbId) { setSaveStatus("error"); return; }
    setSaving(true);
    setSaveStatus(null);
    try {
      await api.put(`admin/ai/question/${q.dbId}/writing-meta`, {
        instructions:         wm.instructions ?? "",
        what_to_include:      Array.isArray(wm.what_to_include) ? wm.what_to_include : [],
        helpful_hints:        Array.isArray(wm.helpful_hints) ? wm.helpful_hints : [],
        show_instructions:    wm.show_instructions ?? true,
        show_what_to_include: wm.show_what_to_include ?? true,
        show_helpful_hints:   wm.show_helpful_hints ?? true,
        min_words:            wm.min_words ?? 75,
        max_words:            wm.max_words ?? 100,
      });
      setSaveStatus("success");
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const instructions    = wm.instructions ?? "";
  const whatToInclude   = Array.isArray(wm.what_to_include) ? wm.what_to_include : [];
  const helpfulHints    = Array.isArray(wm.helpful_hints) ? wm.helpful_hints : [];
  const showInstr       = wm.show_instructions ?? true;
  const showWhat        = wm.show_what_to_include ?? true;
  const showHints       = wm.show_helpful_hints ?? true;
  const minWords        = wm.min_words ?? 75;
  const maxWords        = wm.max_words ?? 100;

  const ToggleRow = ({ label, field, checked }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151" }}>{label}</Typography>
      <FormControlLabel
        control={<Switch size="small" checked={checked} onChange={(e) => update(field, e.target.checked)} />}
        label={<Typography variant="caption" color="text.secondary">{checked ? "Visible" : "Hidden"}</Typography>}
        labelPlacement="start"
        sx={{ m: 0 }}
      />
    </Stack>
  );

  return (
    <Box>
      <QuestionVerificationRow
        q={q}
        batchId={verifyBatchId}
        onBatchUpdated={onVerificationBatchUpdated}
        onQuestionFixed={onQuestionFixed}
        busy={verificationBusy}
        setBusy={setBusy}
      />
      {/* Topic (read-only) */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#e0f2fe", borderRadius: 2, borderLeft: "4px solid #0284c7" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: 0.5 }}>
          ✏️ Writing Topic
        </Typography>
        <Box sx={{ color: "#0c4a6e", mt: 0.5, fontWeight: 500, fontSize: "1rem", lineHeight: 1.6 }}>
          <PreviewLatex html={q.question} />
        </Box>
      </Box>

      {/* Visibility toggles */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
          Section Visibility (shown to students)
        </Typography>
        <ToggleRow label="Instructions" field="show_instructions" checked={showInstr} />
        <ToggleRow label="What to Include" field="show_what_to_include" checked={showWhat} />
        <ToggleRow label="Helpful Hints" field="show_helpful_hints" checked={showHints} />
        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            label="Min words"
            type="number"
            size="small"
            value={minWords}
            onChange={(e) => update("min_words", Number(e.target.value))}
            inputProps={{ min: 10, max: 500 }}
            sx={{ width: 110 }}
          />
          <TextField
            label="Max words"
            type="number"
            size="small"
            value={maxWords}
            onChange={(e) => update("max_words", Number(e.target.value))}
            inputProps={{ min: 10, max: 500 }}
            sx={{ width: 110 }}
          />
        </Stack>
      </Box>

      {/* Instructions edit */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: 0.5 }}>
            📋 Instructions {!showInstr && <Chip label="Hidden" size="small" sx={{ ml: 1, fontSize: 10 }} />}
          </Typography>
        </Stack>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="How to approach the task…"
          value={instructions}
          onChange={(e) => update("instructions", e.target.value)}
          disabled={!showInstr}
        />
      </Box>

      {/* What to Include edit */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: 0.5 }}>
            📝 What to Include {!showWhat && <Chip label="Hidden" size="small" sx={{ ml: 1, fontSize: 10 }} />}
          </Typography>
          <IconButton size="small" onClick={() => update("what_to_include", [...whatToInclude, ""])} disabled={!showWhat}>
            <AddCircleOutlineIcon fontSize="small" sx={{ color: "#ea580c" }} />
          </IconButton>
        </Stack>
        {whatToInclude.map((item, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              value={item}
              placeholder={`Bullet point ${i + 1}`}
              onChange={(e) => {
                const updated = [...whatToInclude];
                updated[i] = e.target.value;
                update("what_to_include", updated);
              }}
              disabled={!showWhat}
            />
            <IconButton size="small" onClick={() => update("what_to_include", whatToInclude.filter((_, j) => j !== i))}>
              <DeleteOutlineIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </IconButton>
          </Stack>
        ))}
      </Box>

      {/* Helpful Hints edit */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#a16207", textTransform: "uppercase", letterSpacing: 0.5 }}>
            💡 Helpful Hints {!showHints && <Chip label="Hidden" size="small" sx={{ ml: 1, fontSize: 10 }} />}
          </Typography>
          <IconButton size="small" onClick={() => update("helpful_hints", [...helpfulHints, ""])} disabled={!showHints}>
            <AddCircleOutlineIcon fontSize="small" sx={{ color: "#ca8a04" }} />
          </IconButton>
        </Stack>
        {helpfulHints.map((hint, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              value={hint}
              placeholder={`Example starter ${i + 1}`}
              onChange={(e) => {
                const updated = [...helpfulHints];
                updated[i] = e.target.value;
                update("helpful_hints", updated);
              }}
              disabled={!showHints}
            />
            <IconButton size="small" onClick={() => update("helpful_hints", helpfulHints.filter((_, j) => j !== i))}>
              <DeleteOutlineIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </IconButton>
          </Stack>
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1 }}>
        📊 Suggested word count: <strong>{minWords}–{maxWords} words</strong>
      </Typography>

      {/* Save Changes */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          disabled={saving || !q.dbId}
          onClick={handleSave}
          sx={{ bgcolor: "#0284c7", "&:hover": { bgcolor: "#0369a1" } }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
        {saveStatus === "success" && (
          <Typography variant="caption" sx={{ color: "#16a34a" }}>✓ Saved to database</Typography>
        )}
        {saveStatus === "error" && (
          <Typography variant="caption" sx={{ color: "#dc2626" }}>Save failed — please retry</Typography>
        )}
        {!q.dbId && (
          <Typography variant="caption" color="text.secondary">(Question not yet saved to DB)</Typography>
        )}
      </Stack>
    </Box>
  );
}

function McqPreview({
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setBusy,
  onError,
}) {
  const [editState, setEditState] = useState({ questionId: null, draft: null, saving: false });
  const [expandedExpl, setExpandedExpl] = useState({});
  const toggleExpl = (id) => setExpandedExpl((prev) => ({ ...prev, [id]: !prev[id] }));

  const beginEdit = (q) => {
    setEditState({
      questionId: q.questionId,
      saving: false,
      draft: {
        question: q.question || "",
        options: Array.isArray(q.options) ? [...q.options] : ["", ""],
        answer: q.answer || "",
        explanation: q.explanation || "",
        visualImageUrl: q.visualImageUrl || q?.visual?.url || q?.visual?.image_url || "",
      },
    });
  };

  const updateDraft = (patch) => {
    setEditState((prev) => ({ ...prev, draft: { ...(prev.draft || {}), ...patch } }));
  };

  const updateDraftOption = (idx, value) => {
    setEditState((prev) => {
      const next = [...(prev.draft?.options || [])];
      next[idx] = value;
      return { ...prev, draft: { ...prev.draft, options: next } };
    });
  };

  const addDraftOption = () => {
    setEditState((prev) => ({ ...prev, draft: { ...prev.draft, options: [...(prev.draft?.options || []), ""] } }));
  };

  const removeDraftOption = (idx) => {
    setEditState((prev) => {
      const next = [...(prev.draft?.options || [])];
      next.splice(idx, 1);
      return { ...prev, draft: { ...prev.draft, options: next } };
    });
  };

  const applyDraftToState = (questionId, draft) => {
    const nextOptions = (draft.options || []).map((v) => (v || "").trim()).filter(Boolean);
    const nextAnswer = (draft.answer || "").trim();
    const safeAnswer = nextAnswer && nextOptions.includes(nextAnswer) ? nextAnswer : "";

    setQuestions((prev) => prev.map((item) => (
      item.questionId === questionId
        ? {
            ...item,
            question: (draft.question || "").trim(),
            options: nextOptions,
            answer: safeAnswer,
            explanation: stripEmptyQuill(draft.explanation),
            visualImageUrl: (draft.visualImageUrl || "").trim() || item.visualImageUrl,
          }
        : item
    )));
  };

  const saveDraft = async (q) => {
    const draft = editState.draft;
    if (!draft) return;
    const nextOptions = (draft.options || []).map((v) => (v || "").trim()).filter(Boolean);
    if ((draft.question || "").trim() === "" || nextOptions.length < 2) {
      onError?.("Manual edit requires a question and at least two options.");
      return;
    }
    const nextAnswer = (draft.answer || "").trim();
    if (nextAnswer && !nextOptions.includes(nextAnswer)) {
      onError?.("Answer must exactly match one of the options.");
      return;
    }

    applyDraftToState(q.questionId, draft);

    if (!q.dbId) {
      setEditState({ questionId: null, draft: null, saving: false });
      return;
    }

    setEditState((prev) => ({ ...prev, saving: true }));
    try {
      await api.put(`admin/ai/question/${q.dbId}/content`, {
        question: (draft.question || "").trim(),
        options: nextOptions,
        answer: nextAnswer || null,
        explanation: stripEmptyQuill(draft.explanation) || null,
        visual: (draft.visualImageUrl || "").trim()
          ? { ...(q.visual || {}), url: (draft.visualImageUrl || "").trim(), image_url: (draft.visualImageUrl || "").trim() }
          : (q.visual || null),
      });
      setEditState({ questionId: null, draft: null, saving: false });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save manual edits.";
      onError?.(msg);
      setEditState((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Box>
      {questions.map((q, idx) => {
        const visualAsset = resolveVisualAsset(q.visualImageUrl, q.visual);
        const isEditing = editState.questionId === q.questionId;
        const draft = isEditing ? editState.draft : null;
        return (
        <Box key={q.questionId || idx} sx={{ mb: 3, border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
          <QuestionVerificationRow
            q={q}
            batchId={verifyBatchId}
            onBatchUpdated={onVerificationBatchUpdated}
            onQuestionFixed={onQuestionFixed}
            busy={verificationBusy}
            setBusy={setBusy}
            onError={onError}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1.5 }}>
            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {idx + 1}.
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PreviewLatex html={q.question} />
            </Box>
          </Box>
          {visualAsset && (
            <Box sx={{ mt: 1, mb: 2, maxWidth: 340 }}>
              {visualAsset.type === "image" ? (
                <QuestionVisualImage src={visualAsset.src} alt={visualAsset.alt || "Visual"} />
              ) : (
                <Box
                  dangerouslySetInnerHTML={{ __html: visualAsset.svg }}
                  sx={{ "& svg": { width: "100%", height: "auto" } }}
                />
              )}
            </Box>
          )}
          <Box sx={{ display: "grid", gap: 1 }}>
            {(q.options || []).map((opt, i) => {
              const isCorrect = q.answer && q.answer === opt;
              return (
                <Box
                  key={opt}
                  sx={{
                    p: 1.5, borderRadius: 1.5, border: "1px solid",
                    borderColor: isCorrect ? "success.main" : "divider",
                    bgcolor: isCorrect ? "rgba(46,125,50,0.07)" : "background.paper",
                    display: "flex", alignItems: "center", gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", minWidth: 22 }}>
                    {String.fromCharCode(65 + i)}.
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PreviewLatex html={opt} />
                  </Box>
                  {isCorrect && <CheckCircleIcon sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }} />}
                </Box>
              );
            })}
          </Box>
          {q.explanation && (
            <>
              <Button
                size="small"
                variant="text"
                onClick={() => toggleExpl(q.questionId)}
                sx={{ mt: 1, textTransform: "none", color: "primary.main", fontWeight: 500, p: 0 }}
              >
                {expandedExpl[q.questionId] ? "Hide explanation" : "Tap to view explanation"}
              </Button>
              <Collapse in={!!expandedExpl[q.questionId]}>
                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid", borderColor: "divider", "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                  <PreviewLatex html={q.explanation} />
                </Box>
              </Collapse>
            </>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => beginEdit(q)}>Edit question</Button>
            {!q.dbId && <Typography variant="caption" color="text.secondary">Unsaved question: edits remain in preview only.</Typography>}
          </Stack>
          {isEditing && draft && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ mb: 1.5, "& .ql-editor": { minHeight: 72 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question</Typography>
                <QuillR2Editor
                  theme="snow"
                  value={draft.question || ""}
                  onChange={(val) => updateDraft({ question: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ background: "#fff" }}
                  showMathTools
                />
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question image</Typography>
                <QuestionImageUploader
                  value={draft.visualImageUrl}
                  onChange={(url) => updateDraft({ visualImageUrl: url })}
                  disabled={editState.saving}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.8 }}>Options</Typography>
              {(draft.options || []).map((opt, optIdx) => (
                <Stack key={`${q.questionId || idx}-edit-${optIdx}`} direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Option ${String.fromCharCode(65 + optIdx)}`}
                    value={opt}
                    onChange={(e) => updateDraftOption(optIdx, e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeDraftOption(optIdx)}
                    disabled={(draft.options || []).length <= 2}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" variant="text" onClick={addDraftOption} sx={{ mb: 1.5 }}>Add option</Button>
              <TextField
                label="Correct answer (must equal an option)"
                fullWidth
                value={draft.answer}
                onChange={(e) => updateDraft({ answer: e.target.value })}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ mb: 1.5, "& .ql-editor img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Explanation</Typography>
                <QuillR2Editor
                  theme="snow"
                  value={draft.explanation || ""}
                  onChange={(val) => updateDraft({ explanation: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ background: "#fff" }}
                  showMathTools
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => saveDraft(q)}
                  disabled={editState.saving}
                >
                  {editState.saving ? "Saving..." : (q.dbId ? "Save to database" : "Apply locally")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditState({ questionId: null, draft: null, saving: false })}
                  disabled={editState.saving}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )})}
    </Box>
  );
}

function FillBlanksPreview({
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setBusy,
  onError,
}) {
  const [editState, setEditState] = useState({ questionId: null, draft: null, saving: false });
  const [expandedExpl, setExpandedExpl] = useState({});
  const toggleExpl = (id) => setExpandedExpl((prev) => ({ ...prev, [id]: !prev[id] }));
  const globalBank = useMemo(() => {
    const words = questions.map((q) => (q.answer || "").trim()).filter(Boolean);
    const copy = [...words];
    let s = 42;
    for (let i = copy.length - 1; i > 0; i--) {
      s = ((s * 1664525 + 1013904223) >>> 0);
      const j = s % (i + 1);
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, [questions]);

  const beginEdit = (q) => {
    setEditState({
      questionId: q.questionId,
      saving: false,
      draft: {
        question: q.question || "",
        answer: q.answer || "",
        explanation: q.explanation || "",
        difficulty: q.difficulty || "Medium",
        visualImageUrl: q.visualImageUrl || q?.visual?.url || q?.visual?.image_url || "",
      },
    });
  };

  const updateDraft = (patch) => {
    setEditState((prev) => ({ ...prev, draft: { ...(prev.draft || {}), ...patch } }));
  };

  const applyDraftToState = (questionId, draft) => {
    setQuestions((prev) => prev.map((item) => (
      item.questionId === questionId
        ? {
            ...item,
            question: (draft.question || "").trim(),
            answer: (draft.answer || "").trim(),
            explanation: stripEmptyQuill(draft.explanation),
            difficulty: draft.difficulty || item.difficulty,
            options: (draft.answer || "").trim() ? [(draft.answer || "").trim()] : (item.options || []),
            visualImageUrl: (draft.visualImageUrl || "").trim() || item.visualImageUrl,
          }
        : item
    )));
  };

  const saveDraft = async (q) => {
    const draft = editState.draft;
    if (!draft) return;
    if ((draft.question || "").trim() === "") {
      onError?.("Question text is required.");
      return;
    }

    applyDraftToState(q.questionId, draft);

    if (!q.dbId) {
      setEditState({ questionId: null, draft: null, saving: false });
      return;
    }

    setEditState((prev) => ({ ...prev, saving: true }));
    try {
      await api.put(`admin/ai/question/${q.dbId}/content`, {
        question: (draft.question || "").trim(),
        options: (draft.answer || "").trim() ? [(draft.answer || "").trim()] : [],
        answer: (draft.answer || "").trim() || null,
        explanation: stripEmptyQuill(draft.explanation) || null,
        difficulty: draft.difficulty || null,
        visual: (draft.visualImageUrl || "").trim()
          ? { ...(q.visual || {}), url: (draft.visualImageUrl || "").trim(), image_url: (draft.visualImageUrl || "").trim() }
          : (q.visual || null),
      });
      setEditState({ questionId: null, draft: null, saving: false });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save manual edits.";
      onError?.(msg);
      setEditState((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Box>
      {globalBank.length > 0 && (
        <Box sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 700 }}>
            Word bank
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {globalBank.map((w, i) => (
              <Chip key={`${w}-${i}`} size="small" label={w} variant="outlined" sx={{ fontWeight: 600 }} />
            ))}
          </Box>
        </Box>
      )}
      {questions.map((q, idx) => {
        const parts = parseBlanks(q.question || "");
        const hasBlankToken = parts.some((p) => p.type === "blank");
        const correct = (q.answer || "").trim();

        return (
          <Box key={q.questionId || idx} sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <QuestionVerificationRow
              q={q}
              batchId={verifyBatchId}
              onBatchUpdated={onVerificationBatchUpdated}
              onQuestionFixed={onQuestionFixed}
              busy={verificationBusy}
              setBusy={setBusy}
              onError={onError}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", lineHeight: 2.2 }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
                {idx + 1}.
              </Typography>
              {parts.map((part, pIdx) => {
                if (part.type === "text") {
                  return (
                    <Box key={pIdx} component="span" sx={{ lineHeight: 2, display: "inline" }}>
                      <PreviewLatex html={part.value} inline />
                    </Box>
                  );
                }
                return (
                  <Box
                    key={pIdx}
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.3,
                      mx: 0.5,
                      borderRadius: 1.5,
                      fontWeight: 700,
                      fontSize: "0.88rem",
                      border: "2px solid #4caf50",
                      bgcolor: "#f0fdf4",
                      color: "#15803d",
                    }}
                  >
                    {correct ? <PreviewLatex html={correct} inline /> : "___"}
                  </Box>
                );
              })}
              {!hasBlankToken && correct && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.3,
                    mx: 0.5,
                    borderRadius: 1.5,
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    border: "2px solid #4caf50",
                    bgcolor: "#f0fdf4",
                    color: "#15803d",
                  }}
                >
                  <PreviewLatex html={correct} inline />
                </Box>
              )}
            </Box>
            {!globalBank.length && correct && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "success.light",
                  bgcolor: "rgba(46, 125, 50, 0.06)",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: "success.dark" }}>
                  Answer:
                </Typography>
                <PreviewLatex html={correct} inline />
              </Box>
            )}
            {q.explanation && (
              <>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => toggleExpl(q.questionId)}
                  sx={{ mt: 1, textTransform: "none", color: "primary.main", fontWeight: 500, p: 0 }}
                >
                  {expandedExpl[q.questionId] ? "Hide explanation" : "Tap to view explanation"}
                </Button>
                <Collapse in={!!expandedExpl[q.questionId]}>
                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid", borderColor: "divider", "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                    <PreviewLatex html={q.explanation} />
                  </Box>
                </Collapse>
              </>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" onClick={() => beginEdit(q)}>Edit question</Button>
              {!q.dbId && <Typography variant="caption" color="text.secondary">Unsaved question: edits remain in preview only.</Typography>}
            </Stack>
            {editState.questionId === q.questionId && editState.draft && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
                <Box sx={{ mb: 1.5, "& .ql-editor": { minHeight: 72 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question</Typography>
                  <QuillR2Editor
                    theme="snow"
                    value={editState.draft.question || ""}
                    onChange={(val) => updateDraft({ question: val })}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={{ background: "#fff" }}
                  />
                </Box>
                <TextField
                  label="Answer"
                  fullWidth
                  value={editState.draft.answer}
                  onChange={(e) => updateDraft({ answer: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  select
                  label="Difficulty"
                  fullWidth
                  value={editState.draft.difficulty || "Medium"}
                  onChange={(e) => updateDraft({ difficulty: e.target.value })}
                  sx={{ mb: 1.5 }}
                >
                  {["Easy", "Medium", "Hard"].map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </TextField>
                <Box sx={{ mb: 1.5, "& .ql-editor img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Explanation</Typography>
                  <QuillR2Editor
                    theme="snow"
                    value={editState.draft.explanation || ""}
                    onChange={(val) => updateDraft({ explanation: val })}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={{ background: "#fff" }}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question image</Typography>
                  <QuestionImageUploader
                    value={editState.draft.visualImageUrl}
                    onChange={(url) => updateDraft({ visualImageUrl: url })}
                    disabled={editState.saving}
                  />
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button size="small" variant="contained" onClick={() => saveDraft(q)} disabled={editState.saving}>
                    {editState.saving ? "Saving..." : (q.dbId ? "Save to database" : "Apply locally")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setEditState({ questionId: null, draft: null, saving: false })}
                    disabled={editState.saving}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function ShortAnswerPreview({
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setBusy,
  onError,
}) {
  const [editState, setEditState] = useState({ questionId: null, draft: null, saving: false });
  const [expandedExpl, setExpandedExpl] = useState({});
  const toggleExpl = (id) => setExpandedExpl((prev) => ({ ...prev, [id]: !prev[id] }));

  const beginEdit = (q) => {
    setEditState({
      questionId: q.questionId,
      saving: false,
      draft: {
        question: q.question || "",
        answer: q.answer || "",
        explanation: q.explanation || "",
        difficulty: q.difficulty || "Medium",
        visualImageUrl: q.visualImageUrl || q?.visual?.url || q?.visual?.image_url || "",
      },
    });
  };

  const updateDraft = (patch) => {
    setEditState((prev) => ({ ...prev, draft: { ...(prev.draft || {}), ...patch } }));
  };

  const applyDraftToState = (questionId, draft) => {
    setQuestions((prev) => prev.map((item) => (
      item.questionId === questionId
        ? {
            ...item,
            question: (draft.question || "").trim(),
            answer: (draft.answer || "").trim(),
            explanation: stripEmptyQuill(draft.explanation),
            difficulty: draft.difficulty || item.difficulty,
            options: (draft.answer || "").trim() ? [(draft.answer || "").trim()] : (item.options || []),
            visualImageUrl: (draft.visualImageUrl || "").trim() || item.visualImageUrl,
          }
        : item
    )));
  };

  const saveDraft = async (q) => {
    const draft = editState.draft;
    if (!draft) return;
    if ((draft.question || "").trim() === "") {
      onError?.("Question text is required.");
      return;
    }

    applyDraftToState(q.questionId, draft);

    if (!q.dbId) {
      setEditState({ questionId: null, draft: null, saving: false });
      return;
    }

    setEditState((prev) => ({ ...prev, saving: true }));
    try {
      await api.put(`admin/ai/question/${q.dbId}/content`, {
        question: (draft.question || "").trim(),
        options: (draft.answer || "").trim() ? [(draft.answer || "").trim()] : [],
        answer: (draft.answer || "").trim() || null,
        explanation: stripEmptyQuill(draft.explanation) || null,
        difficulty: draft.difficulty || null,
        visual: (draft.visualImageUrl || "").trim()
          ? { ...(q.visual || {}), url: (draft.visualImageUrl || "").trim(), image_url: (draft.visualImageUrl || "").trim() }
          : (q.visual || null),
      });
      setEditState({ questionId: null, draft: null, saving: false });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save manual edits.";
      onError?.(msg);
      setEditState((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Box>
      {questions.map((q, idx) => (
        <Box key={q.questionId || idx} sx={{ mb: 2 }}>
          <QuestionVerificationRow
            q={q}
            batchId={verifyBatchId}
            onBatchUpdated={onVerificationBatchUpdated}
        onQuestionFixed={onQuestionFixed}
            busy={verificationBusy}
            setBusy={setBusy}
            onError={onError}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1 }}>
            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {idx + 1}.
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PreviewLatex html={q.question} />
            </Box>
          </Box>
          {q.answer && (
            <Box sx={{ p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1.5, border: "1px solid #bbf7d0" }}>
              <Box sx={{ color: "#15803d", fontSize: "0.875rem", lineHeight: 1.6, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 0.5 }}>
                <strong>Answer:</strong>
                <PreviewLatex html={q.answer} inline />
              </Box>
            </Box>
          )}
          {q.explanation && (
            <>
              <Button
                size="small"
                variant="text"
                onClick={() => toggleExpl(q.questionId)}
                sx={{ mt: 1, textTransform: "none", color: "primary.main", fontWeight: 500, p: 0 }}
              >
                {expandedExpl[q.questionId] ? "Hide explanation" : "Tap to view explanation"}
              </Button>
              <Collapse in={!!expandedExpl[q.questionId]}>
                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid", borderColor: "divider", "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                  <PreviewLatex html={q.explanation} />
                </Box>
              </Collapse>
            </>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => beginEdit(q)}>Edit question</Button>
            {!q.dbId && <Typography variant="caption" color="text.secondary">Unsaved question: edits remain in preview only.</Typography>}
          </Stack>
          {editState.questionId === q.questionId && editState.draft && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ mb: 1.5, "& .ql-editor": { minHeight: 72 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question</Typography>
                <QuillR2Editor
                  theme="snow"
                  value={editState.draft.question || ""}
                  onChange={(val) => updateDraft({ question: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ background: "#fff" }}
                  showMathTools
                />
              </Box>
              <TextField
                label="Answer"
                fullWidth
                value={editState.draft.answer}
                onChange={(e) => updateDraft({ answer: e.target.value })}
                sx={{ mb: 1.5 }}
              />
              <TextField
                select
                label="Difficulty"
                fullWidth
                value={editState.draft.difficulty || "Medium"}
                onChange={(e) => updateDraft({ difficulty: e.target.value })}
                sx={{ mb: 1.5 }}
              >
                {["Easy", "Medium", "Hard"].map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ mb: 1.5, "& .ql-editor img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Explanation</Typography>
                <QuillR2Editor
                  theme="snow"
                  value={editState.draft.explanation || ""}
                  onChange={(val) => updateDraft({ explanation: val })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  style={{ background: "#fff" }}
                  showMathTools
                />
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question image</Typography>
                <QuestionImageUploader
                  value={editState.draft.visualImageUrl}
                  onChange={(url) => updateDraft({ visualImageUrl: url })}
                  disabled={editState.saving}
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => saveDraft(q)}
                  disabled={editState.saving}
                >
                  {editState.saving ? "Saving..." : (q.dbId ? "Save to database" : "Apply locally")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditState({ questionId: null, draft: null, saving: false })}
                  disabled={editState.saving}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function StandardAccordionPreview({
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setBusy,
  onError,
}) {
  const [editState, setEditState] = useState({ questionId: null, draft: null, saving: false });
  const [expandedExpl, setExpandedExpl] = useState({});
  const toggleExpl = (id) => setExpandedExpl((prev) => ({ ...prev, [id]: !prev[id] }));

  const beginEdit = (q) => {
    setEditState({
      questionId: q.questionId,
      saving: false,
      draft: {
        question: q.question || "",
        options: Array.isArray(q.options) ? [...q.options] : [],
        answer: q.answer || "",
        explanation: q.explanation || "",
        difficulty: q.difficulty || "Medium",
        visualImageUrl: q.visualImageUrl || q?.visual?.url || q?.visual?.image_url || "",
      },
    });
  };

  const updateDraft = (patch) => {
    setEditState((prev) => ({ ...prev, draft: { ...(prev.draft || {}), ...patch } }));
  };

  const updateDraftOption = (idx, value) => {
    setEditState((prev) => {
      const next = [...(prev.draft?.options || [])];
      next[idx] = value;
      return { ...prev, draft: { ...prev.draft, options: next } };
    });
  };

  const addDraftOption = () => {
    setEditState((prev) => ({ ...prev, draft: { ...prev.draft, options: [...(prev.draft?.options || []), ""] } }));
  };

  const removeDraftOption = (idx) => {
    setEditState((prev) => {
      const next = [...(prev.draft?.options || [])];
      next.splice(idx, 1);
      return { ...prev, draft: { ...prev.draft, options: next } };
    });
  };

  const applyDraftToState = (questionId, draft) => {
    const nextOptions = (draft.options || []).map((v) => (v || "").trim()).filter(Boolean);
    setQuestions((prev) => prev.map((item) => (
      item.questionId === questionId
        ? {
            ...item,
            question: (draft.question || "").trim(),
            options: nextOptions,
            answer: (draft.answer || "").trim(),
            explanation: stripEmptyQuill(draft.explanation),
            difficulty: draft.difficulty || item.difficulty,
            visualImageUrl: (draft.visualImageUrl || "").trim() || item.visualImageUrl,
          }
        : item
    )));
  };

  const saveDraft = async (q) => {
    const draft = editState.draft;
    if (!draft) return;
    const nextOptions = (draft.options || []).map((v) => (v || "").trim()).filter(Boolean);
    if ((draft.question || "").trim() === "") {
      onError?.("Question text is required.");
      return;
    }
    if (nextOptions.length > 0 && (draft.answer || "").trim() && !nextOptions.includes((draft.answer || "").trim())) {
      onError?.("Answer must match one of the options.");
      return;
    }

    applyDraftToState(q.questionId, draft);

    if (!q.dbId) {
      setEditState({ questionId: null, draft: null, saving: false });
      return;
    }

    setEditState((prev) => ({ ...prev, saving: true }));
    try {
      await api.put(`admin/ai/question/${q.dbId}/content`, {
        question: (draft.question || "").trim(),
        options: nextOptions,
        answer: (draft.answer || "").trim() || null,
        explanation: stripEmptyQuill(draft.explanation) || null,
        difficulty: draft.difficulty || null,
        visual: (draft.visualImageUrl || "").trim()
          ? { ...(q.visual || {}), url: (draft.visualImageUrl || "").trim(), image_url: (draft.visualImageUrl || "").trim() }
          : (q.visual || null),
      });
      setEditState({ questionId: null, draft: null, saving: false });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save manual edits.";
      onError?.(msg);
      setEditState((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Box>
      {questions.map((q, idx) => {
        const visualAsset = resolveVisualAsset(q.visualImageUrl, q.visual);
        return (
          <Accordion key={q.questionId || q.dbId || idx} defaultExpanded={idx === 0} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", pr: 2 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, width: "100%" }}>
                  <Typography component="span" sx={{ fontWeight: 600, flexShrink: 0 }}>{idx + 1}.</Typography>
                  <Box sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                    <PreviewLatex html={q.question} />
                  </Box>
                </Box>
                <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {q.difficulty && <Chip size="small" label={q.difficulty} />}
                  {q.subSubject && <Chip size="small" label={`Topic: ${q.subSubject}`} />}
                  {q.dbId && <Chip size="small" color="success" label="Saved" />}
                </Box>
                {visualAsset && (
                  <Box sx={{ mt: 1.5, maxWidth: 340 }}>
                    {visualAsset.type === "image" ? (
                      <QuestionVisualImage src={visualAsset.src} alt={visualAsset.alt || "Visual"} />
                    ) : (
                      <Box
                        dangerouslySetInnerHTML={{ __html: visualAsset.svg }}
                        sx={{ "& svg": { width: "100%", height: "auto" } }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <QuestionVerificationRow
                q={q}
                batchId={verifyBatchId}
                onBatchUpdated={onVerificationBatchUpdated}
        onQuestionFixed={onQuestionFixed}
                busy={verificationBusy}
                setBusy={setBusy}
                onError={onError}
              />
              <Box sx={{ display: "grid", gap: 1 }}>
                {(q.options || []).map((opt) => (
                  <Box
                    key={opt}
                    sx={{
                      p: 1.2, borderRadius: 1, border: "1px solid",
                      borderColor: q.answer === opt ? "success.main" : "divider",
                      bgcolor: q.answer === opt ? "rgba(46,125,50,0.07)" : "background.paper",
                    }}
                  >
                    <PreviewLatex html={opt} />
                  </Box>
                ))}
              </Box>
              {q.answer && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 0.5 }}>
                    <span>Correct answer:</span>
                    <PreviewLatex html={q.answer} inline />
                  </Box>
                </Alert>
              )}
              {(q.explanation || q.explanation_video_url) && (
                <Box sx={{ mt: 1.5 }}>
                  {q.explanation && (
                    <>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleExpl(q.questionId)}
                        sx={{ textTransform: "none", color: "primary.main", fontWeight: 500, p: 0 }}
                      >
                        {expandedExpl[q.questionId] ? "Hide explanation" : "Tap to view explanation"}
                      </Button>
                      <Collapse in={!!expandedExpl[q.questionId]}>
                        <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider", "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                          <PreviewLatex html={q.explanation} sx={{ "& > div": { whiteSpace: "pre-line", lineHeight: 1.7 } }} />
                        </Box>
                      </Collapse>
                    </>
                  )}
                  {q.explanation_video_url && (
                    <Box sx={{ mt: 1 }}>
                      <VideoExplanationPlayer videoUrl={q.explanation_video_url} questionText={q.question_text} variant="link" />
                    </Box>
                  )}
                </Box>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" variant="outlined" onClick={() => beginEdit(q)}>Edit question</Button>
                {!q.dbId && <Typography variant="caption" color="text.secondary">Unsaved question: edits remain in preview only.</Typography>}
              </Stack>
              {editState.questionId === q.questionId && editState.draft && (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
                  <Box sx={{ mb: 1.5, "& .ql-editor": { minHeight: 72 } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question</Typography>
                    <QuillR2Editor
                      theme="snow"
                      value={editState.draft.question || ""}
                      onChange={(val) => updateDraft({ question: val })}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      style={{ background: "#fff" }}
                      showMathTools
                    />
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Question image</Typography>
                    <QuestionImageUploader
                      value={editState.draft.visualImageUrl}
                      onChange={(url) => updateDraft({ visualImageUrl: url })}
                      disabled={editState.saving}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.8 }}>Options</Typography>
                  {(editState.draft.options || []).map((opt, optIdx) => (
                    <Stack key={`${q.questionId || idx}-std-edit-${optIdx}`} direction="row" spacing={1} sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Option ${String.fromCharCode(65 + optIdx)}`}
                        value={opt}
                        onChange={(e) => updateDraftOption(optIdx, e.target.value)}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeDraftOption(optIdx)}
                        disabled={(editState.draft.options || []).length <= 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size="small" variant="text" onClick={addDraftOption} sx={{ mb: 1.5 }}>Add option</Button>
                  <TextField
                    label="Correct answer"
                    fullWidth
                    value={editState.draft.answer}
                    onChange={(e) => updateDraft({ answer: e.target.value })}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    select
                    label="Difficulty"
                    fullWidth
                    value={editState.draft.difficulty || "Medium"}
                    onChange={(e) => updateDraft({ difficulty: e.target.value })}
                    sx={{ mb: 1.5 }}
                  >
                    {["Easy", "Medium", "Hard"].map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ mb: 1.5, "& .ql-editor img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 1, mt: 1 } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Explanation</Typography>
                    <QuillR2Editor
                      theme="snow"
                      value={editState.draft.explanation || ""}
                      onChange={(val) => updateDraft({ explanation: val })}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      style={{ background: "#fff" }}
                      showMathTools
                    />
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" onClick={() => saveDraft(q)} disabled={editState.saving}>
                      {editState.saving ? "Saving..." : (q.dbId ? "Save to database" : "Apply locally")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditState({ questionId: null, draft: null, saving: false })}
                      disabled={editState.saving}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

function ReadingComprehensionPreview({ questions, setQuestions, verifyBatchId, onVerificationBatchUpdated, onQuestionFixed, verificationBusy, setBusy, onError }) {
  const [editingId, setEditingId] = React.useState(null);
  const [passageDraft, setPassageDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  if (!questions.length) {
    return <Typography color="text.secondary">No reading passages generated.</Typography>;
  }

  const startEdit = (q) => {
    setEditingId(q.questionId);
    setPassageDraft(q.passage || q.passage_text || "");
  };

  const savePassage = async (q) => {
    setQuestions((prev) =>
      prev.map((item) =>
        item.questionId === q.questionId
          ? { ...item, passage: passageDraft, passage_text: passageDraft }
          : item
      )
    );
    if (q.dbId) {
      setSaving(true);
      try {
        await api.put(`admin/ai/question/${q.dbId}/passage`, { passage_text: passageDraft });
        toast.success("Passage saved");
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to save passage");
      } finally {
        setSaving(false);
      }
    }
    setEditingId(null);
  };

  return (
    <Box>
      {questions.map((q, idx) => {
        const passage = q.passage || q.passage_text || "";
        const subQs = Array.isArray(q.sub_questions) ? q.sub_questions : [];
        const audioUrl = q.audio_url || q._audioUrl || "";
        const isEditing = editingId === q.questionId;

        return (
          <Box key={q.questionId || idx} sx={{ mb: 3, pb: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <QuestionVerificationRow
              q={q}
              batchId={verifyBatchId}
              onBatchUpdated={onVerificationBatchUpdated}
              onQuestionFixed={onQuestionFixed}
              busy={verificationBusy}
              setBusy={setBusy}
              onError={onError}
            />
            {audioUrl && (
              <Box sx={{ mb: 2 }}>
                <audio controls src={audioUrl} style={{ width: "100%" }} />
              </Box>
            )}
            <Box sx={{ mb: 2, p: 2, bgcolor: "#faf8f5", borderRadius: 2, borderLeft: "4px solid #8d6e63" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#5d4037", textTransform: "uppercase" }}>
                    Passage
                  </Typography>
                  {q._isPassageRef && (
                    q._isPageExtract
                      ? <Chip size="small" label={`Pages ${q._pageRange?.from}–${q._pageRange?.to}`} color="info" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                      : <Chip size="small" label={`Question Bank #${q._qbId}`} color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                  )}
                </Stack>
                {!isEditing && !q._isPassageRef && (
                  <Button size="small" startIcon={<EditIcon />} onClick={() => startEdit(q)}>
                    Edit
                  </Button>
                )}
              </Stack>
              {isEditing ? (
                <Stack spacing={1}>
                  <TextField
                    multiline
                    minRows={6}
                    fullWidth
                    value={passageDraft}
                    onChange={(e) => setPassageDraft(e.target.value)}
                    sx={{ "& textarea": { fontFamily: "Georgia, serif", lineHeight: 1.8 } }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" disabled={saving} onClick={() => savePassage(q)}>
                      Save
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : passage ? (
                <PassageWithLineNumbers
                  text={passage}
                  showAllFallbackLineNumbers
                  wrapWidth={computePassageWrapWidth(passage, subQs.map((sq) => ({ question_text: sq.question })))}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
              <PreviewLatex html={q.question} />
            </Typography>
            {subQs.map((sq, sqIdx) => (
              <Box key={sqIdx} sx={{ mb: 2, pl: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  {sqIdx + 1}. {sq.question}
                </Typography>
                <Stack spacing={0.5}>
                  {(sq.options || []).map((opt, optIdx) => {
                    const letter = String.fromCharCode(65 + optIdx);
                    const isAnswer = sq.answer && String(sq.answer).trim() === String(opt).trim();
                    return (
                      <Chip
                        key={optIdx}
                        size="small"
                        label={`${letter}. ${opt}`}
                        color={isAnswer ? "success" : "default"}
                        variant={isAnswer ? "filled" : "outlined"}
                        sx={{ justifyContent: "flex-start", height: "auto", py: 0.5, "& .MuiChip-label": { whiteSpace: "normal" } }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function PreviewModalContent({
  formatKey,
  questions,
  setQuestions,
  verifyBatchId,
  onVerificationBatchUpdated,
  onQuestionFixed,
  verificationBusy,
  setVerificationBusy,
  onError,
}) {
  const filtered = questions.filter((q) => q._formatKey === formatKey);
  if (!filtered.length) {
    return <Typography color="text.secondary">No content generated for this section.</Typography>;
  }
  const vprops = {
    verifyBatchId,
    onVerificationBatchUpdated,
    onQuestionFixed,
    verificationBusy,
    setBusy: setVerificationBusy,
    onError,
  };

  const renderFormatPreview = (subset) => {
    switch (formatKey) {
      case "vocab_matching":       return <VocabMatchingPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "writing_composition":  return <WritingCompositionPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "reading_comprehension": return <ReadingComprehensionPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "fill_blanks":          return <FillBlanksPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "short_answer":         return <ShortAnswerPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "multiple_choice":      return <McqPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      case "mcq":                  return <McqPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
      default:                     return <StandardAccordionPreview questions={subset} setQuestions={setQuestions} {...vprops} />;
    }
  };

  const hasTopicGroups = filtered.some((q) => q._allocationKey);
  if (!hasTopicGroups) {
    return renderFormatPreview(filtered);
  }

  const groups = [];
  const seen = new Set();
  filtered.forEach((q) => {
    const key = q._allocationKey || "default";
    if (seen.has(key)) return;
    seen.add(key);
    const subset = filtered.filter((item) => (item._allocationKey || "default") === key);
    const label = q._subtopicLabel
      ? `${q._topicLabel || "Topic"} › ${q._subtopicLabel}`
      : (q._topicLabel || "Topic");
    groups.push({ key, label, subset });
  });

  return (
    <Box>
      {groups.map((group) => (
        <Accordion key={group.key} defaultExpanded={groups.length === 1} sx={{ mb: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {group.label} — {group.subset.length} question{group.subset.length === 1 ? "" : "s"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {renderFormatPreview(group.subset)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function TopicSubtopicTree({
  topics,
  selectedTopicIds,
  selectedSubtopicIds,
  onChangeTopics,
  onChangeSubtopics,
}) {
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const isTopicSelected = (id) => selectedTopicIds.includes(String(id));
  const isSubtopicSelected = (id) => selectedSubtopicIds.includes(String(id));

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const query = search.trim().toLowerCase();
  const filteredTopics = query
    ? topics
        .map((topic) => {
          const topicMatch = topic.name.toLowerCase().includes(query);
          const matchedSubs = (topic.subtopics || []).filter((s) =>
            s.name.toLowerCase().includes(query)
          );
          if (!topicMatch && !matchedSubs.length) return null;
          return { ...topic, subtopics: topicMatch ? topic.subtopics : matchedSubs, _forceOpen: true };
        })
        .filter(Boolean)
    : topics;

  const toggleTopic = (topic) => {
    const id = String(topic.id);
    if (isTopicSelected(id)) {
      onChangeTopics(selectedTopicIds.filter((x) => x !== id));
      const childIds = (topic.subtopics || []).map((s) => String(s.id));
      onChangeSubtopics(selectedSubtopicIds.filter((x) => !childIds.includes(x)));
    } else {
      onChangeTopics([...selectedTopicIds, id]);
    }
  };

  const toggleSubtopic = (topic, subtopic) => {
    const sid = String(subtopic.id);
    const tid = String(topic.id);
    if (isSubtopicSelected(sid)) {
      onChangeSubtopics(selectedSubtopicIds.filter((x) => x !== sid));
    } else {
      onChangeSubtopics([...selectedSubtopicIds, sid]);
      if (!isTopicSelected(tid)) onChangeTopics([...selectedTopicIds, tid]);
    }
  };

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search topics…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
      />
      {filteredTopics.map((topic) => {
        const tid = String(topic.id);
        const subs = topic.subtopics || [];
        const isOpen = topic._forceOpen || !!expanded[tid];
        const childChecked = subs.filter((s) => isSubtopicSelected(String(s.id))).length;
        return (
          <Box key={tid} sx={{ mb: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <IconButton
                size="small"
                onClick={() => toggleExpanded(tid)}
                disabled={!subs.length}
                sx={{ visibility: subs.length ? "visible" : "hidden" }}
              >
                {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
              <FormControlLabel
                sx={{ flex: 1, m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isTopicSelected(tid)}
                    indeterminate={!isTopicSelected(tid) && childChecked > 0}
                    onChange={() => toggleTopic(topic)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {topic.name}
                    {subs.length > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({subs.length})
                      </Typography>
                    )}
                  </Typography>
                }
              />
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ pl: 5 }}>
                {subs.map((sub) => (
                  <FormControlLabel
                    key={String(sub.id)}
                    sx={{ display: "flex", m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={isSubtopicSelected(String(sub.id))}
                        onChange={() => toggleSubtopic(topic, sub)}
                      />
                    }
                    label={<Typography variant="body2">{sub.name}</Typography>}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

function PassageSelectDialog({ open, onClose, onSelect }) {
  const [search, setSearch] = React.useState("");
  const [passages, setPassages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("admin/question-bank", { params: { type: "grouped", per_page: 100, search } })
      .then((res) => setPassages(res.data?.data || []))
      .catch(() => setPassages([]))
      .finally(() => setLoading(false));
  }, [open, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Passage from Question Bank</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search passages…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 0.5 }}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : passages.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No passages found. Add grouped questions to the Question Bank first.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {passages.map((p) => (
              <Box key={p.id} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, cursor: "pointer", "&:hover": { borderColor: "primary.main", bgcolor: "rgba(25,118,210,0.04)" } }}
                onClick={() => { onSelect(p); onClose(); }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  {p.section_title || `Passage #${p.id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {(p.section_instructions || p.question_text || "").slice(0, 120)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const AiAssignmentCreator = () => {
  // Wizard
  const [activeStep, setActiveStep] = useState(0);

  // Metadata
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);

  // Step 1: Subject & Topic
  const [selectedSubjectKey, setSelectedSubjectKey] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedSubTopic, setSelectedSubTopic] = useState("");

  // Reference Books
  const [referenceBooks, setReferenceBooks] = useState([]);
  const [referenceBooksLoading, setReferenceBooksLoading] = useState(false);
  const [selectedReferenceBookId, setSelectedReferenceBookId] = useState("");
  const [bookPageFrom, setBookPageFrom] = useState("");
  const [bookPageTo, setBookPageTo] = useState("");
  const [selectedBookContextText, setSelectedBookContextText] = useState("");
  const [bookExtractLoading, setBookExtractLoading] = useState(false);
  const [bookExtractMeta, setBookExtractMeta] = useState(null);
  const [bookViewer, setBookViewer] = useState(null);
  const [bookPdfBlobUrl, setBookPdfBlobUrl] = useState(null);

  // Ad-hoc context document
  const [attachedContextText, setAttachedContextText] = useState("");
  const [contextDocLoading, setContextDocLoading] = useState(false);
  const [contextDocMeta, setContextDocMeta] = useState(null);

  // Per-topic generation
  const [perTopicMode, setPerTopicMode] = useState(false);
  const [topicAllocations, setTopicAllocations] = useState({});
  const [topicPageRanges, setTopicPageRanges] = useState({});
  const [topicContextTexts, setTopicContextTexts] = useState({});
  const [topicAudioUrls, setTopicAudioUrls] = useState({});
  const [topicAudioExpanded, setTopicAudioExpanded] = useState({});

  // Course linkage (optional)
  const [courseData, setCourseData] = useState({
    academicCourses: [],
    subjects: [],
    assignments: [],
  });
  const [courseSelection, setCourseSelection] = useState({
    academicCourseId: "",
    subjectId: "",
    assignmentId: "",
    planId: "",
    planWeekId: "",
  });
  const [coursePlans, setCoursePlans] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState(null);
  const [courseLoading, setCourseLoading] = useState({
    academicCourses: false,
    subjects: false,
  });
  const [, setCourseError] = useState(null);

  // Step 2: Configure
  const [difficulty, setDifficulty] = useState("Medium");
  const [examBoard, setExamBoard] = useState("GL");
  const [selectedFormats, setSelectedFormats] = useState({});
  const [nativeLanguage, setNativeLanguage] = useState("English");
  const [includeAnswer, setIncludeAnswer] = useState(true);
  const [includeVisuals, setIncludeVisuals] = useState(true);
  const [saveToDatabase, setSaveToDatabase] = useState(true);

  // Reading Comprehension passage reference
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [passageDialogOpen, setPassageDialogOpen] = useState(false);

  // Writing Composition inline prompt preview
  const [writingPrompt, setWritingPrompt] = useState("");
  const [writingPromptEditing, setWritingPromptEditing] = useState(false);

  // Step 3: Generation
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [genProgress, setGenProgress] = useState({ batch: 0, total: 0, label: "" });
  const [genError, setGenError] = useState(null);

  // Step 4: Preview
  const [questions, setQuestions] = useState([]);
  const [allTestIds, setAllTestIds] = useState([]);
  const [downloadKey, setDownloadKey] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);

  // AI verification batch (OpenAI second pass; MCP tools wrap same API)
  const [verifyBatchId, setVerifyBatchId] = useState(null);
  const [verifyBatchData, setVerifyBatchData] = useState(null);
  // Holds the questionId of the row with an action in flight, so one row's request
  // does not disable every other row's buttons.
  const [verificationBusy, setVerificationBusy] = useState(null);
  const [verifyOutcome, setVerifyOutcome] = useState("not_run");
  const [refreshingVerification, setRefreshingVerification] = useState(false);
  const [saveDespiteVerification, setSaveDespiteVerification] = useState(false);

  // Topic rows from `topics` table via admin/ai/question-generator/topics
  const [schoolYears,             setSchoolYears]             = useState([]);
  const [selectedSchoolYearId,    setSelectedSchoolYearId]    = useState("");
  const [standaloneTopics,              setStandaloneTopics]              = useState([]);
  const [selectedStandaloneTopicIds,    setSelectedStandaloneTopicIds]    = useState([]); // multi-select
  const [selectedStandaloneSubtopicIds, setSelectedStandaloneSubtopicIds] = useState([]); // multi-select
  const [loadingStandaloneTopics, setLoadingStandaloneTopics] = useState(false);
  const [subtopicContexts, setSubtopicContexts] = useState({});
  const [subtopicContextExpanded, setSubtopicContextExpanded] = useState({});

  const effectiveSchoolYearIdForStandaloneTopics = useMemo(() => {
    if (courseSelection.academicCourseId && selectedSchoolYear && schoolYears.length) {
      const norm = (s) => String(s || "").trim().toLowerCase();
      const target = norm(selectedSchoolYear);
      const match = schoolYears.find((y) => norm(y.name) === target);
      if (match) return String(match.id);
    }
    return selectedSchoolYearId;
  }, [courseSelection.academicCourseId, selectedSchoolYear, schoolYears, selectedSchoolYearId]);

  const hideStandaloneSchoolYearPicker = Boolean(
    courseSelection.academicCourseId && selectedSchoolYear
  );

  useEffect(() => {
    if (!courseSelection.academicCourseId || !schoolYears.length) return;
    const course = courseData.academicCourses.find(
      (c) => String(c.id) === String(courseSelection.academicCourseId)
    );
    const yearName = course?.school_year;
    if (!yearName) return;
    const norm = (s) => String(s || "").trim().toLowerCase();
    const match = schoolYears.find((y) => norm(y.name) === norm(yearName));
    if (!match) return;
    setSelectedSchoolYear(yearName);
    setSelectedSchoolYearId((prev) => (String(prev) === String(match.id) ? prev : String(match.id)));
  }, [courseSelection.academicCourseId, courseData.academicCourses, schoolYears]);

  useEffect(() => {
    if (!schoolYears.length || selectedSchoolYearId) return;
    const y4 = schoolYears.find((y) => normSchoolYear(y.name) === "year 4");
    if (y4) setSelectedSchoolYearId(String(y4.id));
  }, [schoolYears, selectedSchoolYearId]);

  const filteredAcademicCourses = useMemo(() => {
    const courses = courseData.academicCourses || [];
    if (!selectedSchoolYearId) return courses;
    const yearName = schoolYears.find((y) => String(y.id) === String(selectedSchoolYearId))?.name;
    if (!yearName) return courses;
    const target = normSchoolYear(yearName);
    return courses.filter((c) => normSchoolYear(c.school_year) === target);
  }, [courseData.academicCourses, selectedSchoolYearId, schoolYears]);

  // ── Effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingMeta(true);
        setMetaError(null);
        const res = await api.get("admin/ai/question-generator/metadata");
        if (!mounted) return;
        setMeta(res?.data?.data);
      } catch (e) {
        if (!mounted) return;
        setMetaError(e?.response?.data?.message || e?.message || "Failed to load metadata");
      } finally {
        if (mounted) setLoadingMeta(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const boards = meta?.examBoards;
    if (!boards?.length) return;
    const codes = new Set(boards.map((b) => b.value));
    setExamBoard((prev) => (codes.has(prev) ? prev : boards[0].value));
  }, [meta?.examBoards]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setCourseLoading((p) => ({ ...p, academicCourses: true }));
        const res = await api.get("admin/ca_records", { params: { active_only: 1 } });
        if (!active) return;
        setCourseData((p) => ({ ...p, academicCourses: Array.isArray(res?.data?.data) ? res.data.data : [] }));
      } catch {
        if (active) setCourseError("Failed to load academic courses");
      } finally {
        if (active) setCourseLoading((p) => ({ ...p, academicCourses: false }));
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!courseSelection.academicCourseId) {
      setCoursePlans([]);
      return;
    }
    let active = true;
    api.get(`admin/acdemic-courses/${courseSelection.academicCourseId}/course-plans-with-weeks`)
      .then((r) => { if (active) setCoursePlans(r.data?.plans || []); })
      .catch(() => { if (active) setCoursePlans([]); });
    return () => { active = false; };
  }, [courseSelection.academicCourseId]);

  useEffect(() => {
    if (!courseSelection.academicCourseId) {
      setCourseData((p) => ({ ...p, subjects: [], assignments: [] }));
      return;
    }
    let active = true;
    (async () => {
      try {
        setCourseLoading((p) => ({ ...p, subjects: true }));
        const res = await api.get(`admin/ca_based_weeks_subjects/${courseSelection.academicCourseId}`);
        if (!active) return;
        const payload = res?.data?.data || {};
        setCourseData((p) => ({
          ...p,
          subjects: Array.isArray(payload.subjects) ? payload.subjects : [],
          assignments: Array.isArray(payload.assignments) ? payload.assignments : [],
        }));
      } catch {
        if (active) {
          setCourseError("Failed to load subjects");
          setCourseData((p) => ({ ...p, subjects: [], assignments: [] }));
        }
      } finally {
        if (active) setCourseLoading((p) => ({ ...p, subjects: false }));
      }
    })();
    return () => { active = false; };
  }, [courseSelection.academicCourseId]);

  // Reset subtopic when topic changes
  useEffect(() => {
    setSelectedSubTopic("");
  }, [selectedTopic, courseSelection.assignmentId]);

  // Load standalone topics whenever subject or school year changes
  useEffect(() => {
    const subjectValue = meta?.subjects?.find((s) => s.key === selectedSubjectKey)?.value;
    if (!selectedSubjectKey || !subjectValue) {
      setStandaloneTopics([]);
      setSelectedStandaloneTopicIds([]);
      setSelectedStandaloneSubtopicIds([]);
      setSubtopicContexts({});
      setSubtopicContextExpanded({});
      return;
    }
    let active = true;
    setLoadingStandaloneTopics(true);
    const params = new URLSearchParams({ subject: subjectValue });
    const resolvedYearId = effectiveSchoolYearIdForStandaloneTopics || selectedSchoolYearId;
    if (resolvedYearId) {
      params.set("school_year_id", resolvedYearId);
    } else if (courseSelection.academicCourseId && selectedSchoolYear) {
      params.set("school_year", selectedSchoolYear);
    }
    api.get(`admin/ai/question-generator/topics?${params}`)
      .then((res) => {
        if (!active) return;
        const data = res?.data?.data || {};
        const topics = data.topics || [];
        setStandaloneTopics(topics);
        const allowed = new Set(topics.map((t) => String(t.id)));
        setSelectedStandaloneTopicIds((prev) => prev.filter((id) => allowed.has(String(id))));
        if (data.schoolYears?.length) setSchoolYears(data.schoolYears);
      })
      .catch(() => { if (active) setStandaloneTopics([]); })
      .finally(() => { if (active) setLoadingStandaloneTopics(false); });
    return () => { active = false; };
  }, [
    selectedSubjectKey,
    meta,
    effectiveSchoolYearIdForStandaloneTopics,
    selectedSchoolYearId,
    courseSelection.academicCourseId,
    selectedSchoolYear,
  ]);

  // Load school years once on mount
  useEffect(() => {
    api.get("admin/ai/question-generator/topics?subject=Maths")
      .then((res) => {
        const sy = res?.data?.data?.schoolYears;
        if (Array.isArray(sy) && sy.length) setSchoolYears(sy);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-derive AI subject from course subject selection
  useEffect(() => {
    if (!courseSelection.subjectId || !courseData.subjects.length || !meta?.subjects?.length) return;
    const courseSub = courseData.subjects.find(
      (s) => String(s.id) === String(courseSelection.subjectId)
    );
    if (!courseSub) return;
    const match = meta.subjects.find(
      (s) => s.value?.toLowerCase() === courseSub.name?.toLowerCase()
    );
    if (match && match.key !== selectedSubjectKey) {
      setSelectedSubjectKey(match.key);
      setSelectedTopic("");
      setSelectedFormats({});
      setQuestions([]);
      setAllTestIds([]);
      setGenError(null);
    }
  }, [courseSelection.subjectId, courseData.subjects, meta, selectedSubjectKey]);

  // ── Derived values ───────────────────────────────────────────────────────

  const selectedSubjectMeta = useMemo(
    () => meta?.subjects?.find((s) => s.key === selectedSubjectKey) || null,
    [meta, selectedSubjectKey]
  );

  // Merge subtopics from all selected standalone topics (deduped by lowercase name)
  const standaloneSubtopics = useMemo(() => {
    if (!selectedStandaloneTopicIds.length) return [];
    const seen = new Set();
    const result = [];
    for (const id of selectedStandaloneTopicIds) {
      const topic = standaloneTopics.find((t) => String(t.id) === String(id));
      for (const st of (topic?.subtopics || [])) {
        const key = st.name.toLowerCase();
        if (!seen.has(key)) { seen.add(key); result.push(st); }
      }
    }
    return result;
  }, [selectedStandaloneTopicIds, standaloneTopics]);

  const selectedSubtopicObjects = useMemo(() => {
    const byId = new Map();
    for (const t of standaloneTopics) {
      for (const st of (t.subtopics || [])) {
        byId.set(String(st.id), st);
      }
    }
    return selectedStandaloneSubtopicIds
      .map((id) => byId.get(String(id)))
      .filter(Boolean);
  }, [standaloneTopics, selectedStandaloneSubtopicIds]);

  useEffect(() => {
    setSubtopicContexts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const st of selectedSubtopicObjects) {
        const sid = String(st.id);
        if (next[sid]) continue;
        next[sid] = {
          areas: Array.isArray(st.areas) ? st.areas.join("\n") : "",
          sampleQuestions: typeof st.sample_questions === "string" ? st.sample_questions : "",
        };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [selectedSubtopicObjects]);

  useEffect(() => {
    setSelectedStandaloneSubtopicIds((prev) => {
      const ok = new Set(standaloneSubtopics.map((s) => String(s.id)));
      const next = prev.filter((id) => ok.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [standaloneSubtopics]);

  const resolvedTopicLabel = selectedStandaloneTopicIds.length
    ? standaloneTopics.filter((t) => selectedStandaloneTopicIds.includes(String(t.id))).map((t) => t.name).join(", ")
    : selectedTopic || "";
  const resolvedSubtopicLabel = selectedStandaloneSubtopicIds.length
    ? standaloneSubtopics.filter((s) => selectedStandaloneSubtopicIds.includes(String(s.id))).map((s) => s.name).join(", ")
    : selectedSubTopic || "";

  const selectedReferenceBook = useMemo(
    () => referenceBooks.find((b) => String(b.id) === String(selectedReferenceBookId)) || null,
    [referenceBooks, selectedReferenceBookId]
  );

  useEffect(() => {
    if (!selectedReferenceBook?.id || !selectedReferenceBook?.pdf_url) {
      setBookPdfBlobUrl(null);
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`admin/ai/reference-books/${selectedReferenceBook.id}/pdf`, {
          responseType: "blob",
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBookPdfBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setBookPdfBlobUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedReferenceBook?.id, selectedReferenceBook?.pdf_url]);

  const yearLabelForAllocations = useMemo(() => {
    if (selectedSchoolYear) return selectedSchoolYear;
    if (selectedSchoolYearId) {
      return schoolYears.find((y) => String(y.id) === String(selectedSchoolYearId))?.name || "";
    }
    return "";
  }, [selectedSchoolYear, selectedSchoolYearId, schoolYears]);

  const topicAllocationRows = useMemo(() => {
    if (!perTopicMode) return [];
    const rows = [];

    const pushRow = ({ key, topicId, subtopicId, topicName, subtopicName }) => {
      rows.push({
        key,
        topicId: topicId ? String(topicId) : "",
        subtopicId: subtopicId ? String(subtopicId) : "",
        topicName: topicName || "Topic",
        subtopicName: subtopicName || "",
        yearLabel: yearLabelForAllocations || "All years",
        count: topicAllocations[key] ?? 2,
      });
    };

    if (selectedStandaloneSubtopicIds.length) {
      selectedStandaloneSubtopicIds.forEach((subId) => {
        const sub = standaloneSubtopics.find((s) => String(s.id) === String(subId));
        const topic = standaloneTopics.find((t) => (t.subtopics || []).some((st) => String(st.id) === String(subId)));
        pushRow({
          key: `sub-${subId}`,
          topicId: topic?.id,
          subtopicId: subId,
          topicName: topic?.name,
          subtopicName: sub?.name,
        });
      });
    } else if (selectedStandaloneTopicIds.length) {
      selectedStandaloneTopicIds.forEach((topicId) => {
        const topic = standaloneTopics.find((t) => String(t.id) === String(topicId));
        const subs = topic?.subtopics || [];
        if (subs.length) {
          subs.forEach((st) => {
            pushRow({
              key: `sub-${st.id}`,
              topicId: topic.id,
              subtopicId: st.id,
              topicName: topic.name,
              subtopicName: st.name,
            });
          });
        } else {
          pushRow({
            key: `topic-${topicId}`,
            topicId,
            subtopicId: null,
            topicName: topic?.name,
            subtopicName: "",
          });
        }
      });
    }

    return rows;
  }, [
    perTopicMode,
    selectedStandaloneSubtopicIds,
    selectedStandaloneTopicIds,
    standaloneSubtopics,
    standaloneTopics,
    topicAllocations,
    yearLabelForAllocations,
  ]);

  useEffect(() => {
    if (!perTopicMode) return;
    setTopicAllocations((prev) => {
      let changed = false;
      const next = { ...prev };
      topicAllocationRows.forEach((row) => {
        if (next[row.key] == null) {
          next[row.key] = 2;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [perTopicMode, topicAllocationRows]);

  const passageContextText = selectedPassage
    ? (selectedPassage.section_instructions || selectedPassage.question_text || "")
    : "";
  const effectiveAttachedContextText = passageContextText || selectedBookContextText || attachedContextText || "";

  const currentFormatCards = useMemo(() => {
    if (!selectedSubjectKey) return [];
    if (selectedSubjectKey === "english") return ENGLISH_FORMATS;
    return buildNonEnglishFormats(selectedSubjectMeta?.questionFormats || [], selectedSubjectKey);
  }, [selectedSubjectKey, selectedSubjectMeta]);

  const hasRefBookPassage = selectedSubjectKey === "english" && Boolean(selectedBookContextText);

  const displayedFormatCards = useMemo(
    () => hasRefBookPassage ? currentFormatCards.filter((f) => f.key !== "reading_comprehension") : currentFormatCards,
    [hasRefBookPassage, currentFormatCards]
  );

  const selectedFormatList = useMemo(
    () =>
      displayedFormatCards.filter((f) => selectedFormats[f.key]?.selected === true),
    [displayedFormatCards, selectedFormats]
  );

  const canGenerate = selectedFormatList.length > 0 && !loadingMeta;
  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAcademicCourseChange = (academicCourseId) => {
    const id = academicCourseId ? String(academicCourseId) : "";
    const course = courseData.academicCourses.find((c) => String(c.id) === id);
    setCourseSelection({
      academicCourseId: id,
      subjectId: "",
      assignmentId: "",
      planId: "",
      planWeekId: "",
    });
    if (course?.school_year) {
      setSelectedSchoolYear(course.school_year);
      const match = schoolYears.find((y) => normSchoolYear(y.name) === normSchoolYear(course.school_year));
      if (match) setSelectedSchoolYearId(String(match.id));
    } else {
      setSelectedSchoolYear(null);
    }
  };

  const SUBJECT_TO_BOOK_SUBJECT = {
    english: 'english',
    maths: 'math',
    verbal_reasoning: 'verbal',
    non_verbal_reasoning: 'non_verbal',
  };

  useEffect(() => {
    const bookSubject = SUBJECT_TO_BOOK_SUBJECT[selectedSubjectKey];
    if (!bookSubject) {
      setReferenceBooks([]);
      setSelectedReferenceBookId('');
      setSelectedBookContextText('');
      setBookExtractMeta(null);
      setBookPageFrom('');
      setBookPageTo('');
      return;
    }
    let active = true;
    setReferenceBooksLoading(true);
    api.get('admin/master-form/reference-books-for-ai', { params: { subject: bookSubject } })
      .then((res) => {
        if (active) setReferenceBooks(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => { if (active) setReferenceBooks([]); })
      .finally(() => { if (active) setReferenceBooksLoading(false); });
    return () => { active = false; };
  }, [selectedSubjectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubjectSelect = (key) => {
    if (key === selectedSubjectKey) return;
    setSelectedSubjectKey(key);
    setSelectedTopic("");
    setSelectedSubTopic("");
    setSelectedStandaloneTopicIds([]);
    setSelectedStandaloneSubtopicIds([]);
    setSelectedFormats({});
    setWritingPrompt("");
    setWritingPromptEditing(false);
    setQuestions([]);
    setAllTestIds([]);
    setGenError(null);
    setSelectedReferenceBookId("");
    setSelectedBookContextText("");
    setBookExtractMeta(null);
    setBookPageFrom("");
    setBookPageTo("");
    setAttachedContextText("");
    setContextDocMeta(null);
    setPerTopicMode(false);
    setTopicAllocations({});
    setTopicPageRanges({});
    setTopicContextTexts({});
    setTopicAudioUrls({});
    setTopicAudioExpanded({});
  };

  const handleRefreshWritingPrompt = () => {
    setWritingPromptEditing(false);
    setWritingPrompt((prev) => pickRandomWritingPrompt(prev));
  };

  const handleToggleFormat = (key) => {
    setSelectedFormats((prev) => {
      const wasSelected = !!prev[key]?.selected;
      if (wasSelected) {
        // Deselect, then redistribute remaining types to hit 100
        const next = { ...prev, [key]: { ...prev[key], selected: false } };
        return redistributeToTarget(selectedSubjectKey, next, currentFormatCards);
      }
      const fmt = currentFormatCards.find((f) => f.key === key);
      const desiredCount = prev[key]?.count ?? fmt?.defaultCount ?? 5;
      // Cap count to 60-question limit
      const otherQCount = currentFormatCards.reduce((sum, f) => {
        if (f.key === key || !prev[f.key]?.selected || !f.hasCount) return sum;
        return sum + (prev[f.key]?.count ?? f.defaultCount ?? 1);
      }, 0);
      const cappedCount = Math.max(1, Math.min(desiredCount, MAX_QUESTIONS_PER_TEST - otherQCount));
      // Select the type with its default count, then redistribute all to hit 100
      const next = { ...prev, [key]: { selected: true, count: cappedCount } };
      return redistributeToTarget(selectedSubjectKey, next, currentFormatCards);
    });
    // Auto-fetch a writing prompt on first selection
    if (key === "writing_composition" && !selectedFormats["writing_composition"]?.selected && !writingPrompt) {
      setTimeout(handleRefreshWritingPrompt, 0);
    }
  };

  const handleFormatCountChange = (key, value) => {
    const raw = Number(value) || 1;
    const hardMax = key === "writing_composition" ? 5 : 20;

    setSelectedFormats((prev) => {
      // 1. Enforce 60-question cap
      const otherQCount = currentFormatCards.reduce((sum, f) => {
        if (f.key === key || !prev[f.key]?.selected || !f.hasCount) return sum;
        return sum + (prev[f.key]?.count ?? f.defaultCount ?? 1);
      }, 0);
      const maxForThis = Math.max(1, Math.min(hardMax, MAX_QUESTIONS_PER_TEST - otherQCount));
      const num = Math.max(1, Math.min(maxForThis, raw));

      // 2. Update count, then redistribute all adjustable types to hit 100
      const next = { ...prev, [key]: { ...(prev[key] || {}), count: num } };
      return redistributeToTarget(selectedSubjectKey, next, currentFormatCards);
    });
  };

  const handleStartFresh = () => {
    setActiveStep(0);
    setSelectedSubjectKey("");
    setSelectedTopic("");
    setSelectedSubTopic("");
    setCourseSelection({
      planId: "",
      planWeekId: "",
      academicCourseId: "",
      subjectId: "",
      assignmentId: "",
    });
    setSelectedSchoolYear(null);
    setSelectedFormats({});
    setDifficulty("Medium");
    setExamBoard("GL");
    setIncludeAnswer(true);
    setIncludeVisuals(true);
    setSaveToDatabase(true);
    setWritingPrompt("");
    setWritingPromptEditing(false);
    setQuestions([]);
    setAllTestIds([]);
    setGenProgress({ batch: 0, total: 0, label: "" });
    setGenError(null);
    setPreviewModal(null);
    setDownloadKey(null);
    setVerifyBatchId(null);
    setVerifyBatchData(null);
    setVerificationBusy(null);
    setVerifyOutcome("not_run");
    setSaveDespiteVerification(false);
    setSelectedSchoolYearId("");
    setStandaloneTopics([]);
    setSelectedStandaloneTopicIds([]);
    setSelectedStandaloneSubtopicIds([]);
    setSelectedReferenceBookId("");
    setSelectedBookContextText("");
    setBookExtractMeta(null);
    setBookPageFrom("");
    setBookPageTo("");
    setAttachedContextText("");
    setContextDocMeta(null);
    setPerTopicMode(false);
    setTopicAllocations({});
    setTopicPageRanges({});
    setTopicContextTexts({});
    setTopicAudioUrls({});
    setTopicAudioExpanded({});
  };

  const handleExtractBookPages = async () => {
    if (!selectedReferenceBookId || !bookPageFrom || !bookPageTo) return;
    setBookExtractLoading(true);
    setGenError(null);
    try {
      const res = await api.post("admin/ai/extract-book-pages", {
        referenceBookId: Number(selectedReferenceBookId),
        from_page: Number(bookPageFrom),
        to_page: Number(bookPageTo),
      });
      const data = res?.data?.data;
      setSelectedBookContextText(data?.context_text || "");
      setBookExtractMeta(data);
      setAttachedContextText("");
      setContextDocMeta(null);
      toast.success(`Pages ${data?.from_page}–${data?.to_page} extracted`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to extract book pages";
      toast.error(msg);
    } finally {
      setBookExtractLoading(false);
    }
  };

  const handleExtractBookPagesForRow = async (rowKey) => {
    const range = topicPageRanges[rowKey];
    if (!selectedReferenceBookId || !range?.from || !range?.to) return;
    setBookExtractLoading(true);
    setGenError(null);
    try {
      const res = await api.post("admin/ai/extract-book-pages", {
        referenceBookId: Number(selectedReferenceBookId),
        from_page: Number(range.from),
        to_page: Number(range.to),
      });
      const data = res?.data?.data;
      setTopicContextTexts((prev) => ({ ...prev, [rowKey]: data?.context_text || "" }));
      toast.success(`Pages ${data?.from_page}–${data?.to_page} extracted`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to extract book pages";
      toast.error(msg);
    } finally {
      setBookExtractLoading(false);
    }
  };

  const handleContextDocumentUpload = async (file) => {
    if (!file || selectedBookContextText) return;
    setContextDocLoading(true);
    setGenError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("admin/ai/upload-context-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res?.data?.data;
      setAttachedContextText(data?.context_text || "");
      setContextDocMeta(data);
      toast.success("Document context ready");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to upload document";
      toast.error(msg);
    } finally {
      setContextDocLoading(false);
    }
  };

  const buildGeneratePayload = (fmt, fmtMpq, allocationRow = null) => {
    const forceVisuals = selectedSubjectMeta?.visualsAreRequired === true;
    const allowVisuals = selectedSubjectMeta?.allowVisuals === true;
    const baseCount = fmt.hasCount === false
      ? 1
      : Math.max(1, selectedFormats[fmt.key]?.count || fmt.defaultCount || 1);

    const payload = {
      subject: selectedSubjectMeta?.value || selectedSubjectKey,
      strand: allocationRow?.topicName || resolvedTopicLabel || null,
      subSubject: allocationRow?.subtopicName || resolvedSubtopicLabel || null,
      examBoard,
      difficulty,
      numQuestions: allocationRow ? Math.max(1, allocationRow.count || 2) : baseCount,
      questionType: fmt.label,
      marksPerQuestion: fmtMpq,
      includeAnswer,
      includeVisuals: forceVisuals ? true : allowVisuals ? includeVisuals : false,
      saveToDatabase: false,
    };

    if (courseSelection.academicCourseId) payload.academicCourseId = Number(courseSelection.academicCourseId);
    if (courseSelection.assignmentId) payload.courseAssignmentId = Number(courseSelection.assignmentId);
    if (courseSelection.planWeekId) payload.planWeekId = Number(courseSelection.planWeekId);
    if (courseSelection.subjectId) payload.subjectId = Number(courseSelection.subjectId);

    if (allocationRow?.topicId) {
      payload.standaloneTopicIds = [Number(allocationRow.topicId)];
    } else if (selectedStandaloneTopicIds.length) {
      payload.standaloneTopicIds = selectedStandaloneTopicIds.map(Number);
    }

    if (allocationRow?.subtopicId) {
      payload.standaloneSubtopicIds = [Number(allocationRow.subtopicId)];
    } else if (selectedStandaloneSubtopicIds.length) {
      payload.standaloneSubtopicIds = selectedStandaloneSubtopicIds.map(Number);
    }

    const overrideIds = allocationRow?.subtopicId
      ? [String(allocationRow.subtopicId)]
      : selectedStandaloneSubtopicIds.map(String);
    if (overrideIds.length) {
      const overrides = {};
      for (const sid of overrideIds) {
        const ctx = subtopicContexts[sid];
        if (!ctx) continue;
        const areas = String(ctx.areas || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        const sampleQuestions = String(ctx.sampleQuestions || "").trim();
        overrides[sid] = {
          areas,
          sample_questions: sampleQuestions || null,
        };
      }
      if (Object.keys(overrides).length) {
        payload.subtopicContextOverrides = overrides;
      }
    }

    if (selectedSchoolYear) payload.schoolYear = selectedSchoolYear;
    if (!courseSelection.academicCourseId && selectedSchoolYearId) {
      const yearName = schoolYears.find((y) => String(y.id) === String(selectedSchoolYearId))?.name;
      if (yearName) payload.schoolYear = yearName;
    }
    if (selectedReferenceBookId) payload.referenceBookId = Number(selectedReferenceBookId);

    const rowContext = allocationRow?.key
      ? (topicContextTexts[allocationRow.key] || effectiveAttachedContextText)
      : effectiveAttachedContextText;
    if (rowContext) payload.attachedContextText = rowContext;

    if (hasRefBookPassage) {
      payload.sharedPassage = selectedBookContextText;
    }

    if (allocationRow?.key && topicAudioUrls[allocationRow.key]) {
      payload.topicAudioUrl = topicAudioUrls[allocationRow.key];
    }

    const rowRange = allocationRow?.key ? topicPageRanges[allocationRow.key] : null;
    if (rowRange?.from) {
      payload.bookPageFrom = Number(rowRange.from);
      payload.bookPageTo = Number(rowRange.to || rowRange.from);
    } else if (selectedBookContextText && bookExtractMeta?.from_page) {
      payload.bookPageFrom = bookExtractMeta.from_page;
      payload.bookPageTo = bookExtractMeta.to_page;
    }

    return payload;
  };

  const handleVerificationBatchUpdated = (data) => {
    setVerifyBatchData(data);
    setVerifyBatchId(data?.id ?? null);
    setQuestions((prev) => applyVerificationToQuestions(prev, data));
  };

  const refreshVerificationStatus = async () => {
    if (!verifyBatchId) return;
    setRefreshingVerification(true);
    try {
      const res = await api.get(`admin/ai/verify-batch/${verifyBatchId}`);
      const data = res?.data?.data;
      if (data?.status === "completed" || data?.status === "failed") {
        setVerifyOutcome(data.status);
        handleVerificationBatchUpdated(data);
      } else {
        toast.info("Verification is still running. Try again in a moment.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not read the verification status.");
    } finally {
      setRefreshingVerification(false);
    }
  };

  // Questions with unresolved findings, so the summary can link straight to them.
  const flaggedQuestions = useMemo(() => {
    const per = verifyBatchData?.verification_report?.per_question;
    if (!per) return [];
    const counters = {};
    return questions
      .map((q) => {
        const fmt = q._formatKey || "";
        counters[fmt] = (counters[fmt] || 0) + 1;
        const v = per[q.questionId];
        const status = v?.effective_overall_status || v?.overall_status;
        if (!v || status === "passed") return null;
        const issueCount = (v.issues || []).filter((i) => !i?.resolved && !i?.dismissed && !i?.verified_by_admin).length;
        return {
          questionId: q.questionId,
          formatKey: fmt,
          formatLabel: q._formatLabel || fmt,
          index: counters[fmt],
          status,
          issueCount,
        };
      })
      .filter(Boolean);
  }, [questions, verifyBatchData]);

  const handleQuestionFixed = (questionId, fixedQuestion) => {
    setQuestions((prev) => prev.map((q) =>
      q.questionId === questionId
        ? { ...q, ...fixedQuestion, questionId, _formatKey: q._formatKey, _formatLabel: q._formatLabel, _marksPerQ: q._marksPerQ }
        : q
    ));
  };

  const buildSaveTestPayload = (questionsToSave, combinedType) => {
    const payload = {
      subject: selectedSubjectMeta?.value || selectedSubjectKey,
      strand: resolvedTopicLabel || null,
      subSubject: resolvedSubtopicLabel || null,
      examBoard,
      difficulty,
      questionType: combinedType,
      questions: prepareQuestionsForSave(questionsToSave),
      marksOverrides: Object.fromEntries(
        selectedFormatList
          .filter((f) => selectedFormats[f.key]?.marksPerQ != null)
          .map((f) => [f.key, selectedFormats[f.key].marksPerQ])
      ),
    };
    if (courseSelection.academicCourseId) payload.academicCourseId = Number(courseSelection.academicCourseId);
    if (courseSelection.assignmentId) payload.courseAssignmentId = Number(courseSelection.assignmentId);
    if (courseSelection.planWeekId) payload.planWeekId = Number(courseSelection.planWeekId);
    if (courseSelection.subjectId) payload.subjectId = Number(courseSelection.subjectId);
    if (selectedStandaloneTopicIds.length) {
      payload.standaloneTopicIds = selectedStandaloneTopicIds.map(Number);
    }
    if (selectedStandaloneSubtopicIds.length) {
      payload.standaloneSubtopicIds = selectedStandaloneSubtopicIds.map(Number);
    }
    if (selectedSchoolYearId) {
      const yearName = schoolYears.find((y) => String(y.id) === String(selectedSchoolYearId))?.name;
      if (yearName) payload.schoolYear = yearName;
    }
    return payload;
  };

  const applySavedTestResponse = (saved, questionList) => {
    const next = [...questionList];
    const finalTestIds = [];
    if (saved?.testId) {
      finalTestIds.push({
        testId: saved.testId,
        testName: saved.testName || "",
        formatKey: "combined",
        formatLabel: "Complete Test Paper",
      });
      const dbIds = saved.questionDbIds || {};
      next.forEach((q) => {
        q.testId = saved.testId;
        q.testName = saved.testName;
        if (q.questionId && dbIds[q.questionId]) {
          q.dbId = dbIds[q.questionId];
        }
      });
    }
    return { next, finalTestIds };
  };

  const handleGenerate = async () => {
    const formats = selectedFormatList;
    if (!formats.length) return;

    setActiveStep(2);
    setLoadingGenerate(true);
    setGenError(null);
    setQuestions([]);
    setAllTestIds([]);
    setVerifyBatchId(null);
    setVerifyBatchData(null);

    const allocationRows = perTopicMode && topicAllocationRows.length ? topicAllocationRows : [null];
    const total = formats.length * allocationRows.length;

    setGenProgress({
      batch: 0,
      total,
      label: perTopicMode && topicAllocationRows.length
        ? `${formats.length} type${formats.length === 1 ? "" : "s"} × ${topicAllocationRows.length} topic${topicAllocationRows.length === 1 ? "" : "s"}`
        : `${formats.length} type${formats.length === 1 ? "" : "s"} in parallel`,
    });

    const formatRequests = [];
    formats.forEach((fmt) => {
      if (fmt.key === "reading_comprehension") return; // passage is context, not generated
      const fmtMpq = selectedFormats[fmt.key]?.marksPerQ ?? getTypeConfig(selectedSubjectKey, fmt.key).marksPerQ ?? null;
      allocationRows.forEach((allocationRow) => {
        const payload = buildGeneratePayload(fmt, fmtMpq, allocationRow);
        formatRequests.push({
          fmt,
          fmtMpq,
          allocationRow,
          request: api.post("admin/ai/generate-question", payload),
        });
      });
    });

    const settled = await Promise.allSettled(formatRequests.map((r) => r.request));

    const allQs = [];
    const failedLabels = [];
    const failedMessages = new Set();

    settled.forEach((result, i) => {
      const { fmt, fmtMpq, allocationRow } = formatRequests[i];
      if (result.status === "fulfilled") {
        const data = result.value?.data?.data || [];
        const tagged = data.map((q) => ({
          ...q,
          difficulty,
          _formatKey: fmt.key,
          _formatLabel: fmt.label,
          _marksPerQ: fmtMpq,
          _allocationKey: allocationRow?.key || null,
          _topicLabel: allocationRow?.topicName || q.strand || null,
          _subtopicLabel: allocationRow?.subtopicName || q.subSubject || null,
          _audioUrl: topicAudioUrls[allocationRow?.key] || q.audio_url || null,
          audio_url: q.audio_url || topicAudioUrls[allocationRow?.key] || null,
        }));
        allQs.push(...tagged);
      } else {
        const msg = extractApiErrorMessage(result.reason, "Generation failed");
        const topicSuffix = allocationRow
          ? ` (${allocationRow.topicName}${allocationRow.subtopicName ? ` › ${allocationRow.subtopicName}` : ""})`
          : "";
        failedLabels.push(`"${fmt.label}"${topicSuffix}: ${msg}`);
        failedMessages.add(msg);
      }
    });

    // Every request failing the same way is one problem (quota, timeout, offline) — say it once.
    if (failedLabels.length > 1 && failedMessages.size === 1) {
      failedLabels.length = 0;
      failedLabels.push([...failedMessages][0]);
    }
    if (failedMessages.size) {
      toast.error([...failedMessages][0]);
    }

    if (hasRefBookPassage) {
      for (const q of allQs) {
        if (!q.passage) q.passage = selectedBookContextText;
      }
    }

    if (selectedFormats["reading_comprehension"]?.selected) {
      if (selectedPassage) {
        allQs.unshift({
          questionId: `rc-passage-${selectedPassage.id}`,
          _formatKey: "reading_comprehension",
          _formatLabel: "Reading Comprehension",
          passage: selectedPassage.section_instructions || selectedPassage.question_text || "",
          question: selectedPassage.section_title || "Read the passage and answer the questions below.",
          sub_questions: [],
          _isPassageRef: true,
          _qbId: selectedPassage.id,
        });
      } else if (bookExtractMeta && selectedBookContextText) {
        allQs.unshift({
          questionId: `rc-pages-${bookExtractMeta.from_page}-${bookExtractMeta.to_page}`,
          _formatKey: "reading_comprehension",
          _formatLabel: "Reading Comprehension",
          passage: selectedBookContextText,
          question: `Read pages ${bookExtractMeta.from_page}–${bookExtractMeta.to_page} and answer the questions below.`,
          sub_questions: [],
          _isPassageRef: true,
          _isPageExtract: true,
          _pageRange: { from: bookExtractMeta.from_page, to: bookExtractMeta.to_page },
        });
      }
    }

    if (failedLabels.length > 0 && allQs.length === 0) {
      setGenError(`Failed to generate questions:\n${failedLabels.join("\n")}`);
      setLoadingGenerate(false);
      return;
    }

    if (failedLabels.length > 0) {
      setGenError(`Some types failed (continuing with successful ones):\n${failedLabels.join("\n")}`);
    }

    let mergedQs = allQs;
    let verifiedBatch = null;
    let verifyFailed = false;

    if (allQs.length > 0) {
      setGenProgress({ batch: total, total, label: "Verifying questions…" });
      try {
        const createRes = await api.post("admin/ai/verify-batch", {
          questions: allQs,
          context: {
            subject: selectedSubjectMeta?.value || selectedSubjectKey,
            examBoard,
            difficulty,
            topic: resolvedTopicLabel || null,
            subtopic: resolvedSubtopicLabel || null,
            academicCourseId: courseSelection.academicCourseId || null,
            courseAssignmentId: courseSelection.assignmentId || null,
            planWeekId: courseSelection.planWeekId || null,
            subjectId: courseSelection.subjectId || null,
          },
        });
        const batchId = createRes?.data?.data?.batch_id;
        if (!batchId) {
          throw new Error("Verification batch was not created.");
        }
        const polled = await pollAiVerifyBatch(batchId);
        setVerifyBatchId(batchId);

        if (polled?.status === "poll_timeout") {
          verifyFailed = true;
          setVerifyOutcome("poll_timeout");
          setVerifyBatchData(null);
        } else {
          verifiedBatch = polled;
          setVerifyOutcome(polled?.status === "failed" ? "failed" : "completed");
          setVerifyBatchData(polled);
          mergedQs = applyVerificationToQuestions(allQs, polled);
          verifyFailed = polled?.status === "failed";
        }
      } catch (ve) {
        verifyFailed = true;
        setVerifyOutcome("failed");
        setVerifyBatchId(null);
        setVerifyBatchData(null);
        setGenError(
          (prev) =>
            `${prev ? `${prev}\n\n` : ""}Verification failed: ${extractApiErrorMessage(ve, "Verification failed")}. Questions are shown without verification.`
        );
      }
    }

    const finalTestIds = [];
    if (saveToDatabase && mergedQs.length > 0 && !verifyFailed) {
      const canSave = verifiedBatch && verifiedBatch.can_save;
      if (verifiedBatch && !canSave) {
        setGenError(
          (prev) =>
            `${prev ? `${prev}\n\n` : ""}AI verification reported unresolved issues. Review each question, use Dismiss / Mark issue OK / Accept question, or click "Save to database" after acknowledging the override below.`
        );
      } else {
        setGenProgress({ batch: total, total, label: "Saving to database…" });

        const formatLabels = formats.map((f) => f.label);
        const combinedType =
          formats.length === 1
            ? formats[0].label
            : `Mixed (${formatLabels.join(", ")})`;

        try {
          const saveRes = await api.post(
            "admin/ai/save-test",
            buildSaveTestPayload(mergedQs, combinedType)
          );
          const saved = saveRes?.data?.data;
          const applied = applySavedTestResponse(saved, mergedQs);
          finalTestIds.push(...applied.finalTestIds);
          if (applied.finalTestIds.length) {
            toast.success("Test saved to database");
          }
        } catch (e) {
          const msg = extractApiErrorMessage(e, "Save failed");
          setGenError(`Questions generated but could not be saved: ${msg}`);
        }
      }
    }

    setQuestions(mergedQs);
    setAllTestIds(finalTestIds);
    setLoadingGenerate(false);
    setActiveStep(3);
  };

  const handleDownloadPdf = async (testId, testName, variant, optKey) => {
    if (!testId) return;
    const key = optKey || `${testId}-${variant}`;
    setDownloadKey(key);
    try {
      const params = new URLSearchParams();
      params.set("variant", variant);
      const response = await api.get(`admin/assign/test/${testId}/download-pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${slugify(testName || "ai-test")}-${variant.replace(/_/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setGenError(e?.response?.data?.message || e?.message || "Failed to download PDF");
    } finally {
      setDownloadKey(null);
    }
  };

  const handleSaveToDatabaseNow = async () => {
    if (!questions.length || !saveToDatabase) return;
    if (verifyBatchData && !verifyBatchData.can_save && !saveDespiteVerification) {
      const msg = "Resolve verification issues, or enable \"Save despite verification issues\" below.";
      setGenError(msg);
      toast.error(msg);
      return;
    }
    setLoadingGenerate(true);
    setGenError(null);
    try {
      const formats = selectedFormatList;
      const formatLabels = formats.map((f) => f.label);
      const combinedType =
        formats.length === 1
          ? formats[0].label
          : `Mixed (${formatLabels.join(", ")})`;

      const saveRes = await api.post(
        "admin/ai/save-test",
        buildSaveTestPayload(questions, combinedType)
      );
      const saved = saveRes?.data?.data;
      if (!saved?.testId) {
        throw new Error("Save completed but no test id was returned.");
      }
      const { next, finalTestIds } = applySavedTestResponse(saved, questions);
      setQuestions(next);
      setAllTestIds(finalTestIds);
      setGenError(null);
      toast.success(saved.testName ? `Saved: ${saved.testName}` : "Test saved to database");
    } catch (e) {
      const msg = extractApiErrorMessage(e, "Save failed");
      setGenError(msg);
      toast.error(msg);
    } finally {
      setLoadingGenerate(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Page header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: "1.35rem", sm: "2.125rem" } }}>AI Assignment Creator</Typography>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleStartFresh} color="inherit" sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
          Start Fresh
        </Button>
      </Stack>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, display: { xs: "none", sm: "flex" } }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4, display: { xs: "flex", sm: "none" } }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {metaError && <Alert severity="error" sx={{ mb: 3 }}>{metaError}</Alert>}

      {/* ─── Step 0: Course → Subject → Topic ───────────────────────────── */}
      {activeStep === 0 && (
        <Box>

          {/* ── 1. School Year & Course Name ── */}
          {(schoolYears.length > 0 || courseData.academicCourses.length > 0) && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <CardContent>
                <Grid container spacing={2}>
                  {schoolYears.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        <MenuBookIcon sx={{ color: "primary.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>School Year</Typography>
                      </Box>
                      <FormControl fullWidth>
                        <InputLabel>School Year</InputLabel>
                        <Select
                          label="School Year"
                          value={selectedSchoolYearId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setSelectedSchoolYearId(nextId);
                            const yearName = schoolYears.find((y) => String(y.id) === String(nextId))?.name;
                            setSelectedSchoolYear(yearName || null);
                            if (courseSelection.academicCourseId) {
                              const course = courseData.academicCourses.find(
                                (c) => String(c.id) === String(courseSelection.academicCourseId)
                              );
                              if (course && yearName && normSchoolYear(course.school_year) !== normSchoolYear(yearName)) {
                                handleAcademicCourseChange("");
                              }
                            }
                          }}
                        >
                          <MenuItem value=""><em>All years</em></MenuItem>
                          {schoolYears.map((y) => (
                            <MenuItem key={y.id} value={String(y.id)}>{y.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {hideStandaloneSchoolYearPicker && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                          Auto-filled from the academic course — adjust if needed.
                        </Typography>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <ClassIcon sx={{ color: "primary.main" }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Course Name</Typography>
                    </Box>
                    <FormControl fullWidth disabled={courseLoading.academicCourses}>
                      <InputLabel>Course Name</InputLabel>
                      <Select
                        label="Course Name"
                        value={courseSelection.academicCourseId || ""}
                        onChange={(e) => handleAcademicCourseChange(e.target.value)}
                      >
                        <MenuItem value=""><em>No course (standalone test)</em></MenuItem>
                        {filteredAcademicCourses.map((c) => (
                          <MenuItem key={c.id} value={String(c.id)}>
                            {courseNameOf(c) || `Course #${c.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {courseSelection.academicCourseId && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                        Saved tests link to this course. Plan / week can be set on the next step.
                      </Typography>
                    )}
                    {!courseLoading.academicCourses && selectedSchoolYearId && !filteredAcademicCourses.length && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.75, display: "block" }}>
                        No active courses for the selected school year.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* ── 3. AI Subject cards (auto-selected from course, or manual) ── */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Select Subject</Typography>
            {courseSelection.subjectId ? (
              <Typography variant="body2" color="text.secondary">
                Auto-detected from the course — or click another card to override.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Choose a course above to auto-detect, or click a subject directly.
              </Typography>
            )}
          </Box>

          {loadingMeta ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton variant="rounded" height={120} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(meta?.subjects || []).map((subject) => {
                const cfg = SUBJECT_CONFIG[subject.key] || { Icon: QuizIcon, color: "#555", bg: "#f5f5f5" };
                const { Icon } = cfg;
                const isSelected = selectedSubjectKey === subject.key;
                const topicCount = (subject.subCategories || []).reduce(
                  (a, sc) => a + (sc.topics || []).length, 0
                );
                return (
                  <Grid item xs={12} sm={6} key={subject.key}>
                    <Box
                      onClick={() => handleSubjectSelect(subject.key)}
                      sx={{
                        p: 2.5, borderRadius: 3, border: "2px solid",
                        borderColor: isSelected ? cfg.color : "divider",
                        bgcolor: isSelected ? cfg.bg : "background.paper",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: cfg.color, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
                        position: "relative", height: "100%",
                      }}
                    >
                      {isSelected && (
                        <CheckCircleIcon sx={{ position: "absolute", top: 12, right: 12, color: cfg.color, fontSize: 20 }} />
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                        <Box sx={{ p: 1.2, borderRadius: 2, display: "flex", bgcolor: isSelected ? cfg.color : "rgba(0,0,0,0.06)" }}>
                          <Icon sx={{ fontSize: 22, color: isSelected ? "#fff" : cfg.color }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {subject.label.split(" (")[0]}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">{topicCount} topics</Typography>
                      <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {subject.visualsAreRequired && <Chip size="small" label="Visuals required" color="info" />}
                        {subject.allowVisuals && !subject.visualsAreRequired && (
                          <Chip size="small" label="Visuals available" sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }} />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* ── Reference Book selector (shown when books are available for selected subject) ── */}
          {selectedSubjectKey && (referenceBooksLoading || referenceBooks.length > 0) && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Reference Book <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                </Typography>
                <FormControl fullWidth size="small" disabled={referenceBooksLoading}>
                  <InputLabel>Select a book for thematic inspiration</InputLabel>
                  <Select
                    label="Select a book for thematic inspiration"
                    value={selectedReferenceBookId}
                    onChange={(e) => {
                      setSelectedReferenceBookId(e.target.value);
                      setSelectedBookContextText("");
                      setBookExtractMeta(null);
                      setBookPageFrom("");
                      setBookPageTo("");
                    }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {referenceBooks.map((book) => (
                      <MenuItem key={book.id} value={book.id}>
                        {book.is_primary ? '★ ' : ''}{book.title}{book.author ? ` — ${book.author}` : ''}{book.genre ? ` (${book.genre})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedReferenceBook && (selectedReferenceBook.pdf_url || selectedReferenceBook.epub_url) && (
                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
                      {selectedReferenceBook.pdf_url && <Chip size="small" label="PDF" color="primary" variant="outlined" />}
                      {selectedReferenceBook.epub_url && <Chip size="small" label="EPUB" color="secondary" variant="outlined" />}
                      {selectedReferenceBook.page_count && (
                        <Chip size="small" label={`${selectedReferenceBook.page_count} pages`} variant="outlined" />
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setBookViewer({
                          title: selectedReferenceBook.title,
                          pdfUrl: bookPdfBlobUrl || selectedReferenceBook.pdf_url,
                          epubUrl: selectedReferenceBook.epub_url,
                        })}
                      >
                        Open viewer
                      </Button>
                    </Stack>

                    {selectedReferenceBook.pdf_url && (
                      <Box sx={{ height: { xs: 500, md: 800 }, mb: 2, borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider", bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {bookPdfBlobUrl ? (
                          <iframe
                            title={selectedReferenceBook.title}
                            src={bookPdfBlobUrl}
                            style={{ width: "100%", height: "100%", border: "none" }}
                          />
                        ) : (
                          <CircularProgress size={22} />
                        )}
                      </Box>
                    )}

                    {selectedReferenceBook.pdf_url && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          label="From page"
                          value={bookPageFrom}
                          onChange={(e) => setBookPageFrom(e.target.value)}
                          inputProps={{ min: 1, max: selectedReferenceBook.page_count || undefined }}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="To page"
                          value={bookPageTo}
                          onChange={(e) => setBookPageTo(e.target.value)}
                          inputProps={{ min: 1, max: selectedReferenceBook.page_count || undefined }}
                          sx={{ width: 120 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={bookExtractLoading || !bookPageFrom || !bookPageTo}
                          onClick={handleExtractBookPages}
                          startIcon={bookExtractLoading ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                          Extract pages
                        </Button>
                      </Stack>
                    )}

                    {bookExtractMeta && selectedBookContextText && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          color="success"
                          size="small"
                          label={`Pages ${bookExtractMeta.from_page}–${bookExtractMeta.to_page} extracted (${bookExtractMeta.char_count?.toLocaleString?.() || bookExtractMeta.char_count} chars)`}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedBookContextText("");
                            setBookExtractMeta(null);
                          }}
                        >
                          Clear
                        </Button>
                      </Stack>
                    )}

                    {!selectedReferenceBook.pdf_url && !selectedReferenceBook.epub_url && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Questions draw thematic inspiration from this book&apos;s vocabulary and setting.
                      </Typography>
                    )}
                  </Box>
                )}

                {selectedReferenceBookId && !selectedReferenceBook?.pdf_url && !selectedReferenceBook?.epub_url && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Questions will draw thematic inspiration from the selected book&apos;s vocabulary, characters, and setting.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {selectedSubjectKey && !selectedBookContextText && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Or attach your own document
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Upload a PDF or EPUB chapter scan to use as AI context (alternative to reference book pages).
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button variant="outlined" component="label" disabled={contextDocLoading}>
                    {contextDocLoading ? "Processing…" : "Choose PDF / EPUB"}
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.epub"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleContextDocumentUpload(file);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                  {contextDocMeta && (
                    <>
                      <Chip
                        size="small"
                        color="success"
                        label={`${contextDocMeta.file_name} — context ready`}
                      />
                      {contextDocMeta.file_url && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setBookViewer({
                            title: contextDocMeta.file_name,
                            pdfUrl: contextDocMeta.file_url?.endsWith(".pdf") ? contextDocMeta.file_url : null,
                            epubUrl: contextDocMeta.file_url?.endsWith(".epub") ? contextDocMeta.file_url : null,
                          })}
                        >
                          View
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setAttachedContextText("");
                          setContextDocMeta(null);
                        }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── 4. Exam Board & Difficulty (always visible once a subject is chosen) ── */}
          {selectedSubjectKey && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Grid container spacing={3} alignItems="flex-start">
                  {/* Exam Board */}
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Exam Board
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(meta?.examBoards || []).map((b) => (
                        <Tooltip key={b.key} title={b.label} arrow>
                          <Button
                            size="small"
                            variant={examBoard === b.value ? "contained" : "outlined"}
                            onClick={() => setExamBoard(b.value)}
                            sx={{
                              minWidth: 72, fontWeight: 700, borderRadius: 2,
                              fontSize: "0.82rem",
                              ...(examBoard !== b.value && { borderColor: "divider", color: "text.primary" }),
                            }}
                          >
                            {b.value}
                          </Button>
                        </Tooltip>
                      ))}
                    </Box>
                    {examBoard && (() => {
                      const board = (meta?.examBoards || []).find((b) => b.value === examBoard);
                      return board ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                          {board.focus ? `${board.label} — ${board.focus}` : board.label}
                        </Typography>
                      ) : null;
                    })()}
                  </Grid>

                  {/* Divider */}
                  <Grid item xs={12} md="auto" sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch" }}>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  </Grid>

                  {/* Difficulty */}
                  <Grid item xs={12} md>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Difficulty
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(meta?.difficulties || ["Easy", "Medium", "Hard"]).map((d) => {
                        const color = d === "Easy" ? "#16a34a" : d === "Hard" ? "#dc2626" : "#d97706";
                        const isActive = difficulty === d;
                        return (
                          <Button
                            key={d}
                            size="small"
                            variant={isActive ? "contained" : "outlined"}
                            onClick={() => setDifficulty(d)}
                            sx={{
                              minWidth: 88, fontWeight: 700, borderRadius: 2,
                              fontSize: "0.82rem",
                              ...(isActive
                                ? { bgcolor: color, "&:hover": { bgcolor: color }, boxShadow: `0 2px 8px ${color}55` }
                                : { borderColor: color, color }),
                            }}
                          >
                            {d}
                          </Button>
                        );
                      })}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                      Controls question complexity for AI generation
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* ── 5. Topic / Subtopic tree — pulled from the master `topics` table ── */}
          {selectedSubjectKey && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Select Topic</Typography>
                {loadingStandaloneTopics && <CircularProgress size={16} />}
              </Box>
              <Card sx={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <CardContent sx={{ maxHeight: 420, overflow: "auto" }}>
                  {!standaloneTopics.length && !loadingStandaloneTopics ? (
                    <Typography variant="body2" color="text.secondary">
                      No topics found for this subject {selectedSchoolYearId ? "and year" : ""}.
                    </Typography>
                  ) : (
                    <TopicSubtopicTree
                      topics={standaloneTopics}
                      selectedTopicIds={selectedStandaloneTopicIds}
                      selectedSubtopicIds={selectedStandaloneSubtopicIds}
                      onChangeTopics={setSelectedStandaloneTopicIds}
                      onChangeSubtopics={setSelectedStandaloneSubtopicIds}
                    />
                  )}
                </CardContent>
              </Card>
              {!selectedStandaloneTopicIds.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Leave empty for mixed topics across the subject.
                </Typography>
              )}

              {selectedSubtopicObjects.length > 0 && (
                <Card sx={{ mt: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Subtopic Focus & Examples
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Prefills from the Master Form. Refine per subtopic before AI generation — used for skill areas, worked explanations, and image instructions.
                    </Typography>
                    {selectedSubtopicObjects.map((st) => {
                      const sid = String(st.id);
                      const ctx = subtopicContexts[sid] || { areas: "", sampleQuestions: "" };
                      const expanded = subtopicContextExpanded[sid] !== false;
                      return (
                        <Accordion
                          key={sid}
                          disableGutters
                          expanded={expanded}
                          onChange={(_, isExpanded) =>
                            setSubtopicContextExpanded((prev) => ({ ...prev, [sid]: isExpanded }))
                          }
                          sx={{
                            mb: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "8px !important",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {st.name}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <TextField
                                label="Focus Areas (one per line)"
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                value={ctx.areas}
                                onChange={(e) =>
                                  setSubtopicContexts((prev) => ({
                                    ...prev,
                                    [sid]: { ...ctx, areas: e.target.value },
                                  }))
                                }
                                placeholder={"e.g. Identify polygons\nCalculate perimeter"}
                              />
                              <TextField
                                label="Sample Questions / Instructions"
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                value={ctx.sampleQuestions}
                                onChange={(e) =>
                                  setSubtopicContexts((prev) => ({
                                    ...prev,
                                    [sid]: { ...ctx, sampleQuestions: e.target.value },
                                  }))
                                }
                                placeholder="Example questions or generation instructions for this subtopic…"
                              />
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {(selectedStandaloneTopicIds.length > 0 || selectedStandaloneSubtopicIds.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={perTopicMode}
                        onChange={(e) => setPerTopicMode(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Generate per topic / subtopic"
                  />

                  {perTopicMode && topicAllocationRows.length > 0 && (
                    <Card sx={{ mt: 1.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                      <CardContent sx={{ py: "12px !important" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          Topic allocation
                        </Typography>
                        <Stack spacing={1}>
                          {topicAllocationRows.map((row) => {
                            const rowRange = topicPageRanges[row.key] || {};
                            const rowContext = topicContextTexts[row.key];
                            const audioExpanded = !!topicAudioExpanded[row.key];
                            return (
                            <Stack
                              key={row.key}
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ sm: "center" }}
                              sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.yearLabel} · {row.topicName}
                                  {row.subtopicName ? ` › ${row.subtopicName}` : ""}
                                </Typography>
                                {selectedReferenceBookId && (
                                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
                                    <TextField
                                      size="small"
                                      type="number"
                                      placeholder="From"
                                      value={rowRange.from ?? ""}
                                      onChange={(e) => setTopicPageRanges((prev) => ({
                                        ...prev,
                                        [row.key]: { ...prev[row.key], from: e.target.value, to: prev[row.key]?.to ?? "" },
                                      }))}
                                      inputProps={{ min: 1, style: { width: 48, textAlign: "center" } }}
                                      sx={{ width: 72 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">–</Typography>
                                    <TextField
                                      size="small"
                                      type="number"
                                      placeholder="To"
                                      value={rowRange.to ?? ""}
                                      onChange={(e) => setTopicPageRanges((prev) => ({
                                        ...prev,
                                        [row.key]: { ...prev[row.key], to: e.target.value, from: prev[row.key]?.from ?? "" },
                                      }))}
                                      inputProps={{ min: 1, style: { width: 48, textAlign: "center" } }}
                                      sx={{ width: 72 }}
                                    />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={bookExtractLoading || !rowRange.from || !rowRange.to}
                                      onClick={() => handleExtractBookPagesForRow(row.key)}
                                    >
                                      Extract
                                    </Button>
                                    <Chip
                                      size="small"
                                      color={rowContext ? "success" : "default"}
                                      label={
                                        rowContext && rowRange.from
                                          ? `Pages ${rowRange.from}–${rowRange.to} ready`
                                          : "Not extracted"
                                      }
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => setTopicAudioExpanded((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                                      title="Audio URL"
                                    >
                                      <AudiotrackIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                )}
                                {audioExpanded && (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Audio URL (R2-hosted MP3)"
                                    value={topicAudioUrls[row.key] || ""}
                                    onChange={(e) => setTopicAudioUrls((prev) => ({ ...prev, [row.key]: e.target.value }))}
                                    sx={{ mt: 0.75 }}
                                  />
                                )}
                              </Box>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <IconButton
                                  size="small"
                                  onClick={() => setTopicAllocations((prev) => ({
                                    ...prev,
                                    [row.key]: Math.max(1, (prev[row.key] ?? 2) - 1),
                                  }))}
                                >
                                  <Typography variant="body2">−</Typography>
                                </IconButton>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={topicAllocations[row.key] ?? row.count}
                                  onChange={(e) => setTopicAllocations((prev) => ({
                                    ...prev,
                                    [row.key]: Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                                  }))}
                                  inputProps={{ min: 1, max: 10, style: { width: 36, textAlign: "center" } }}
                                  sx={{ width: 72 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => setTopicAllocations((prev) => ({
                                    ...prev,
                                    [row.key]: Math.min(10, (prev[row.key] ?? 2) + 1),
                                  }))}
                                >
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="text.secondary">per type</Typography>
                              </Stack>
                            </Stack>
                            );
                          })}
                        </Stack>
                        {(() => {
                          const perTypeTotal = topicAllocationRows.reduce(
                            (sum, row) => sum + (topicAllocations[row.key] ?? row.count ?? 2),
                            0
                          );
                          const formatCount = selectedFormatList.length || 1;
                          const grandTotal = perTypeTotal * formatCount;
                          return (
                            <Chip
                              size="small"
                              sx={{ mt: 1.5 }}
                              color={grandTotal > MAX_QUESTIONS_PER_TEST ? "error" : "default"}
                              label={`Total ≈ ${grandTotal} questions (${perTypeTotal} × ${formatCount} format${formatCount === 1 ? "" : "s"})`}
                            />
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}

                  {perTopicMode && !topicAllocationRows.length && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
                      Select at least one topic or subtopic to use per-topic generation.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={!selectedSubjectKey || loadingMeta}
              onClick={() => setActiveStep(1)}
              endIcon={<ArrowForwardIosIcon sx={{ fontSize: 13 }} />}
              sx={{ minWidth: 140, fontWeight: 700 }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* ─── Step 1: Configure ────────────────────────────────────────────── */}
      {activeStep === 1 && (
        <Box>
          {coursePlans.length > 0 && (
            <Card sx={{ mb: 2, border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
              <CardContent sx={{ py: "12px !important", px: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Plan / Batch
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 280 }, width: { xs: "100%", sm: "auto" } }}>
                    <InputLabel>Select plan</InputLabel>
                    <Select
                      value={courseSelection.planId || ""}
                      label="Select plan"
                      onChange={(e) => setCourseSelection((prev) => ({ ...prev, planId: e.target.value, planWeekId: "" }))}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {coursePlans.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name} · {p.total_weeks}w
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {courseSelection.planId && (
                    <Typography variant="caption" color="text.secondary">
                      Pick a week below — the generated test attaches there.
                    </Typography>
                  )}
                </Box>
                {courseSelection.planId && (() => {
                  const plan = coursePlans.find((p) => String(p.id) === String(courseSelection.planId));
                  if (!plan) return null;
                  return (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", maxHeight: 180, overflowY: "auto" }}>
                      {plan.weeks.map((w) => {
                        const selected = String(courseSelection.planWeekId) === String(w.id);
                        return (
                          <Chip
                            key={w.id}
                            size="small"
                            label={`Wk ${w.week_number} (${w.start_date} → ${w.end_date}) · ${w.allocated_test_count}t`}
                            color={selected ? "primary" : "default"}
                            variant={selected ? "filled" : "outlined"}
                            onClick={() => setCourseSelection((prev) => ({ ...prev, planWeekId: w.id }))}
                            sx={{ cursor: "pointer" }}
                          />
                        );
                      })}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Exam board & Difficulty summary (read-only, set on Step 0) */}
          <Card sx={{ mb: 2, bgcolor: "grey.50", boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
            <CardContent sx={{ py: "12px !important", px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Mode
                </Typography>
                <Chip
                  label={`Exam Board: ${examBoard}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                {(() => {
                  const color = difficulty === "Easy" ? "success" : difficulty === "Hard" ? "error" : "warning";
                  return (
                    <Chip
                      label={`Difficulty: ${difficulty}`}
                      size="small"
                      color={color}
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  );
                })()}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setActiveStep(0)}
                  sx={{ ml: "auto", fontSize: "0.75rem", textTransform: "none", color: "text.secondary" }}
                >
                  ← Change on Step 1
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Question type cards */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Question Types</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {selectedFormatList.length > 0 && (() => {
                    const totalQ = computeTotalQuestions(selectedFormats, displayedFormatCards);
                    const totalM = computeTotalMarks(selectedSubjectKey, selectedFormats, displayedFormatCards);
                    const qColor = totalQ >= MAX_QUESTIONS_PER_TEST ? "error" : totalQ >= MAX_QUESTIONS_PER_TEST * 0.9 ? "warning" : "default";
                    const mColor = totalM === 100 ? "success" : totalM > 100 ? "error" : "warning";
                    return (
                      <>
                        <Chip
                          label={`${totalQ} / ${MAX_QUESTIONS_PER_TEST} questions`}
                          color={qColor}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                        <Chip
                          label={`Total: ${totalM} / 100 marks`}
                          color={mColor}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </>
                    );
                  })()}
                  {selectedFormatList.length > 0 && <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select one or more types. Max {MAX_QUESTIONS_PER_TEST} questions and 100 marks per test — marks auto-adjust to reach 100.
              </Typography>
              {hasRefBookPassage && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "#faf8f5", borderRadius: 2, borderLeft: "4px solid #8d6e63" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#5d4037", textTransform: "uppercase", display: "block", mb: 0.75 }}>
                    Shared Reading Passage
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "Georgia, serif", lineHeight: 1.8, color: "#4e342e", whiteSpace: "pre-wrap" }}>
                    {selectedBookContextText.length > 600 ? `${selectedBookContextText.slice(0, 600)}…` : selectedBookContextText}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                    All questions generated below will reference this passage. Reading Comprehension is hidden — the passage is shared across all types.
                  </Typography>
                </Box>
              )}
              <Grid container spacing={1.5}>
                {displayedFormatCards.map((fmt) => {
                  const isSelected = !!selectedFormats[fmt.key]?.selected;
                  const count = selectedFormats[fmt.key]?.count ?? fmt.defaultCount;
                  const { Icon } = fmt;
                  const isWriting = fmt.key === "writing_composition";
                  const isVocab = fmt.key === "vocab_matching";
                  const typeCfg = getTypeConfig(selectedSubjectKey, fmt.key);
                  // Use stored effective marksPerQ (may be adjusted from default to hit 100 total)
                  const curMpq = isVocab ? 1 : (selectedFormats[fmt.key]?.marksPerQ ?? typeCfg.marksPerQ ?? 1);
                  const defaultMpq = typeCfg.marksPerQ ?? 1;
                  const isReduced = !isVocab && !isWriting && isSelected && curMpq < defaultMpq;
                  const isBoosted = !isVocab && !isWriting && isSelected && curMpq > defaultMpq;
                  const isAdjusted = isReduced || isBoosted;
                  const typeMarks = isVocab ? count : isWriting ? curMpq : curMpq * count;
                  return (
                    <Grid item xs={12} sm={isWriting ? 12 : 6} key={fmt.key}>
                      <Box
                        onClick={() => handleToggleFormat(fmt.key)}
                        sx={{
                          p: 2, borderRadius: 2, border: "2px solid",
                          borderColor: isSelected ? (isReduced ? "warning.main" : "primary.main") : "divider",
                          bgcolor: isSelected ? (isReduced ? "rgba(237,108,2,0.05)" : "rgba(25,118,210,0.05)") : "background.paper",
                          cursor: "pointer",
                          transition: "border-color 0.15s, background-color 0.15s",
                          "&:hover": { borderColor: isSelected ? (isReduced ? "warning.main" : "primary.main") : "primary.light" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Icon sx={{ fontSize: 19, color: isSelected ? (isReduced ? "warning.main" : "primary.main") : "text.secondary" }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt.label}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Chip
                              label={isVocab ? `1 mark/pair` : isReduced ? `${curMpq} marks each ↓` : isBoosted ? `${curMpq} marks each ↑` : typeCfg.marksLabel}
                              size="small"
                              variant={isSelected ? "filled" : "outlined"}
                              color={isSelected ? (isReduced ? "warning" : "primary") : "default"}
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                            {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: isReduced ? "warning.main" : "primary.main", flexShrink: 0 }} />}
                          </Box>
                        </Box>

                        {isSelected && fmt.key !== "reading_comprehension" && (
                          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              label={isVocab
                                ? `${count} pairs = ${typeMarks} marks`
                                : isWriting
                                  ? `${typeMarks} marks total`
                                  : isAdjusted
                                    ? `${count} × ${curMpq} = ${typeMarks} marks (default ${defaultMpq}/q)`
                                    : `${count} × ${curMpq} = ${typeMarks} marks`}
                              size="small"
                              color={isReduced ? "warning" : "success"}
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                            <Chip
                              label={`~${Math.round((typeCfg.duration ?? 30) / 60 * 10) / 10} min each`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          </Box>
                        )}

                        {isSelected && fmt.key === "reading_comprehension" && (
                          <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                            {selectedPassage ? (
                              <Box sx={{ p: 1, bgcolor: "#faf8f5", borderRadius: 1, border: "1px solid #bcaaa4" }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#5d4037", display: "block" }}>
                                  {selectedPassage.section_title || "Passage selected"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                                  {(selectedPassage.section_instructions || selectedPassage.question_text || "").slice(0, 80)}…
                                </Typography>
                                <Button size="small" sx={{ mt: 0.5, p: 0, fontSize: "0.7rem", minWidth: 0, textTransform: "none" }} onClick={() => setPassageDialogOpen(true)}>
                                  Change passage
                                </Button>
                                {bookExtractMeta && (
                                  <Button size="small" sx={{ mt: 0.5, ml: 1, p: 0, fontSize: "0.7rem", minWidth: 0, textTransform: "none" }} onClick={() => setSelectedPassage(null)}>
                                    Use pages {bookExtractMeta.from_page}–{bookExtractMeta.to_page} instead
                                  </Button>
                                )}
                              </Box>
                            ) : bookExtractMeta ? (
                              <Box sx={{ p: 1, bgcolor: "#f3f8ff", borderRadius: 1, border: "1px solid #90caf9" }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#1565c0", display: "block" }}>
                                  Pages {bookExtractMeta.from_page}–{bookExtractMeta.to_page} (book extract)
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Extracted text will be used as the passage
                                </Typography>
                                <Button size="small" sx={{ mt: 0.5, p: 0, fontSize: "0.7rem", minWidth: 0, textTransform: "none" }} onClick={() => setPassageDialogOpen(true)}>
                                  Select from QB instead
                                </Button>
                              </Box>
                            ) : (
                              <Button size="small" variant="outlined" onClick={() => setPassageDialogOpen(true)}
                                sx={{ fontSize: "0.75rem", textTransform: "none" }}>
                                Select passage from Question Bank
                              </Button>
                            )}
                          </Box>
                        )}

                        {isSelected && fmt.hasCount && fmt.key !== "reading_comprehension" && (() => {
                          const otherQ = currentFormatCards.reduce((sum, f) => {
                            if (f.key === fmt.key || !selectedFormats[f.key]?.selected || !f.hasCount) return sum;
                            return sum + (selectedFormats[f.key]?.count ?? f.defaultCount ?? 1);
                          }, 0);
                          const dynamicMax = Math.max(1, Math.min(
                            fmt.key === "writing_composition" ? 5 : 20,
                            MAX_QUESTIONS_PER_TEST - otherQ,
                          ));
                          return (
                            <Box
                              sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {fmt.countLabel || (isVocab ? "Pairs" : "Questions")}:
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={count}
                                onChange={(e) => handleFormatCountChange(fmt.key, e.target.value)}
                                inputProps={{
                                  min: 1,
                                  max: dynamicMax,
                                  style: { width: 36, textAlign: "center", padding: "4px" },
                                }}
                                sx={{ width: 72 }}
                              />
                              {dynamicMax < (fmt.key === "writing_composition" ? 5 : 20) && (
                                <Typography variant="caption" color="text.secondary">
                                  max {dynamicMax}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}

                        {isSelected && fmt.hasNativeLanguage && (
                          <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                              Student's Native Language
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={nativeLanguage}
                                onChange={(e) => setNativeLanguage(e.target.value)}
                                displayEmpty
                              >
                                {NATIVE_LANGUAGES.map((lang) => (
                                  <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}

                        {/* Writing Composition inline prompt preview */}
                        {isSelected && isWriting && (
                          <Box
                            sx={{ mt: 2 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {writingPromptEditing ? (
                              <TextField
                                multiline
                                minRows={2}
                                fullWidth
                                size="small"
                                value={writingPrompt}
                                onChange={(e) => setWritingPrompt(e.target.value)}
                                placeholder="Enter a custom writing prompt…"
                                autoFocus
                                sx={{ mb: 1 }}
                              />
                            ) : writingPrompt ? (
                              <Box
                                sx={{
                                  p: 1.5, borderRadius: 1.5,
                                  border: "1px solid", borderColor: "primary.light",
                                  bgcolor: "#f0f7ff", mb: 1,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.primary" }}>
                                  "{writingPrompt}"
                                </Typography>
                              </Box>
                            ) : null}

                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                                onClick={handleRefreshWritingPrompt}
                                sx={{ textTransform: "none", fontSize: 12 }}
                              >
                                {writingPrompt ? "Regenerate" : "Generate prompt"}
                              </Button>
                              {writingPrompt && !writingPromptEditing && (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                                  onClick={() => setWritingPromptEditing(true)}
                                  sx={{ textTransform: "none", fontSize: 12 }}
                                >
                                  Edit
                                </Button>
                              )}
                              {writingPromptEditing && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setWritingPromptEditing(false)}
                                  sx={{ textTransform: "none", fontSize: 12 }}
                                >
                                  Done
                                </Button>
                              )}
                            </Box>
                          </Box>
                        )}

                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {canGenerate && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={handleGenerate}
                  sx={{ fontWeight: 700, borderColor: "success.dark", color: "success.dark" }}
                >
                  Generate Test
                </Button>
              }
            >
              {selectedFormatList.length} type{selectedFormatList.length === 1 ? "" : "s"} selected — you can generate now, or adjust options below first.
            </Alert>
          )}

          {/* Options */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>Options</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={includeAnswer} onChange={(e) => setIncludeAnswer(e.target.checked)} />}
                  label="Include answers"
                />
                {selectedSubjectMeta?.allowVisuals && (
                  <Tooltip
                    title={selectedSubjectMeta?.visualsAreRequired ? "Visuals are always required for this subject" : ""}
                    disableHoverListener={!selectedSubjectMeta?.visualsAreRequired}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSubjectMeta?.visualsAreRequired ? true : includeVisuals}
                          disabled={!!selectedSubjectMeta?.visualsAreRequired}
                          onChange={(e) => setIncludeVisuals(e.target.checked)}
                        />
                      }
                      label={selectedSubjectMeta?.visualsAreRequired ? "Include visuals (required)" : "Include visuals"}
                    />
                  </Tooltip>
                )}
                <FormControlLabel
                  control={<Checkbox checked={saveToDatabase} onChange={(e) => setSaveToDatabase(e.target.checked)} />}
                  label="Save to database"
                />
              </Box>
              {!saveToDatabase && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  PDF downloads are only available when "Save to database" is enabled.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" color="inherit" startIcon={<RefreshIcon />} onClick={handleStartFresh}>
                Start Fresh
              </Button>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Back
              </Button>
            </Box>
            <Tooltip
              title={!canGenerate ? "Select at least one question type to continue" : ""}
              disableHoverListener={canGenerate}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!canGenerate}
                  onClick={handleGenerate}
                  sx={{
                    minWidth: 180, fontWeight: 700,
                    bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" },
                    "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                  }}
                >
                  Generate Test
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* ─── Step 2: AI Generation ────────────────────────────────────────── */}
      {activeStep === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 3 }}>
          {genError ? (
            <>
              <Alert severity="error" sx={{ maxWidth: 520 }}>{genError}</Alert>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleStartFresh}>
                Start Over
              </Button>
            </>
          ) : (
            <>
              {/* Animated brain circle */}
              <Box
                sx={{
                  width: 144, height: 144, borderRadius: "50%",
                  border: "3px solid #ede9fe",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                  animation: "pulseRing 2s ease-out infinite",
                  "@keyframes pulseRing": {
                    "0%": { boxShadow: "0 0 0 0 rgba(124,58,237,0.25)" },
                    "70%": { boxShadow: "0 0 0 24px rgba(124,58,237,0)" },
                    "100%": { boxShadow: "0 0 0 0 rgba(124,58,237,0)" },
                  },
                }}
              >
                <PsychologyIcon
                  sx={{
                    fontSize: 72, color: "#7c3aed",
                    animation: "pulseBrain 2s ease-in-out infinite",
                    "@keyframes pulseBrain": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.07)" },
                    },
                  }}
                />
                <AutoAwesomeIcon
                  sx={{
                    position: "absolute", top: 10, right: 10, fontSize: 20, color: "#f59e0b",
                    animation: "sparkle 1.6s ease-in-out infinite",
                    "@keyframes sparkle": {
                      "0%, 100%": { opacity: 1, transform: "scale(1) rotate(0deg)" },
                      "50%": { opacity: 0.4, transform: "scale(0.75) rotate(20deg)" },
                    },
                  }}
                />
              </Box>

              {/* Gradient title */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800, textAlign: "center",
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Generating Test
              </Typography>

              {/* Subtitle */}
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center" }}>
                {genProgress.total > 0
                  ? genProgress.label === "Saving to database…"
                    ? "Saving to database…"
                    : genProgress.label === "Verifying questions…"
                    ? "Running AI verification on stems, answers, and visuals…"
                    : `Generating ${genProgress.label} simultaneously…`
                  : "Starting…"}
              </Typography>

              {/* Bouncing dots */}
              <Box sx={{ display: "flex", gap: 1 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 10, height: 10, borderRadius: "50%", bgcolor: "#8b5cf6",
                      animation: "bounceDot 1.4s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                      "@keyframes bounceDot": {
                        "0%, 80%, 100%": { transform: "scale(0)", opacity: 0.3 },
                        "40%": { transform: "scale(1)", opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Box>

              <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleStartFresh}>
                Start Over
              </Button>
            </>
          )}
        </Box>
      )}

      {/* ─── Step 3: Preview & Download ──────────────────────────────────── */}
      {activeStep === 3 && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Generated Content</Typography>

          {genError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenError(null)}>
              {genError}
            </Alert>
          )}

          {/* Summary chips */}
          <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
            {selectedSubjectMeta && (
              <Chip label={selectedSubjectMeta.label.split(" (")[0]} color="primary" />
            )}
            <Chip label={difficulty} variant="outlined" />
            <Chip label={examBoard} variant="outlined" />
            {resolvedTopicLabel && (
              <Tooltip title={resolvedTopicLabel} placement="top" enterDelay={400}>
                <Chip
                  label={resolvedTopicLabel}
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: "min(100%, 420px)",
                    height: "auto",
                    py: 0.5,
                    "& .MuiChip-label": { whiteSpace: "normal", display: "block", lineHeight: 1.35 },
                  }}
                />
              </Tooltip>
            )}
            {resolvedSubtopicLabel && (
              <Tooltip title={`Subtopic: ${resolvedSubtopicLabel}`} placement="top" enterDelay={400}>
                <Chip
                  label={`Subtopic: ${resolvedSubtopicLabel}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: "min(100%, 420px)",
                    height: "auto",
                    py: 0.5,
                    "& .MuiChip-label": { whiteSpace: "normal", display: "block", lineHeight: 1.35 },
                  }}
                />
              </Tooltip>
            )}
          {bookExtractMeta && (
            <Chip
              size="small"
              variant="outlined"
              label={`Book pages ${bookExtractMeta.from_page}–${bookExtractMeta.to_page}`}
            />
          )}
          {contextDocMeta && !bookExtractMeta && (
            <Chip size="small" variant="outlined" label={`Context: ${contextDocMeta.file_name}`} />
          )}
          <Chip label={`${questions.reduce((sum, q) => sum + (q._formatKey === "vocab_matching" ? (q.options?.length || 1) : 1), 0)} questions`} color="success" variant="outlined" />
            <Chip
              label={`${computeTotalMarks(selectedSubjectKey, selectedFormats, displayedFormatCards)} marks`}
              color="warning"
              variant="outlined"
            />
          </Box>

          {verifyOutcome === "poll_timeout" && !verifyBatchData && (
            <Alert
              severity="info"
              sx={{ mb: 3 }}
              action={
                <Button size="small" onClick={refreshVerificationStatus} disabled={refreshingVerification}>
                  {refreshingVerification ? "Checking…" : "Refresh status"}
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                AI verification is still running
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The check has not finished yet — it has not failed. Refresh to pick up the result, or save without it.
              </Typography>
            </Alert>
          )}

          {verifyBatchData?.verification_report?.summary && (
            <Alert
              severity={verifyBatchData.can_save ? "success" : "warning"}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                AI verification (stem, answer & visual check)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Passed: {verifyBatchData.verification_report.summary.passed} · Issues:{" "}
                {verifyBatchData.verification_report.summary.issues_found} · Needs review:{" "}
                {verifyBatchData.verification_report.summary.needs_human_review} · Open issues:{" "}
                {verifyBatchData.verification_report.summary.unresolved_issue_count}
              </Typography>
              {verifyBatchData.status === "failed" && verifyBatchData.error_message && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                  {verifyBatchData.error_message}
                </Typography>
              )}
              {flaggedQuestions.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.25, lineHeight: 1.5 }}>
                    Questions needing attention — tap one to open it:
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.75 }}>
                    {flaggedQuestions.map((f) => (
                      <Chip
                        key={f.questionId}
                        size="small"
                        color={f.status === "issues_found" ? "error" : "warning"}
                        variant="outlined"
                        clickable
                        onClick={() => setPreviewModal({ formatKey: f.formatKey, formatLabel: f.formatLabel })}
                        label={`${f.formatLabel} · Q${f.index}${f.issueCount ? ` · ${f.issueCount}` : ""}`}
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Alert>
          )}


          {/* Per-topic breakdown */}
          {questions.some((q) => q._allocationKey) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Questions by topic</Typography>
                {(() => {
                  const groups = [];
                  const seen = new Set();
                  questions.forEach((q) => {
                    const key = q._allocationKey;
                    if (!key || seen.has(key)) return;
                    seen.add(key);
                    const subset = questions.filter((item) => item._allocationKey === key);
                    const label = q._subtopicLabel
                      ? `${q._topicLabel || "Topic"} › ${q._subtopicLabel}`
                      : (q._topicLabel || "Topic");
                    groups.push({ key, label, count: subset.length });
                  });
                  return groups.map((group) => (
                    <Accordion key={group.key} sx={{ mb: 0.5, "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {group.label} — {group.count} question{group.count === 1 ? "" : "s"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary">
                          Open a format below (Tap to Preview) to review questions for this topic.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ));
                })()}
              </CardContent>
            </Card>
          )}

          {/* Format summary list */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              {displayedFormatCards
                .filter((f) => selectedFormats[f.key]?.selected === true)
                .map((fmt, idx, arr) => {
                  const { Icon } = fmt;
                  const fmtQs = questions.filter((q) => q._formatKey === fmt.key);
                  // Vocab matching: 1 question object with N word-definition pairs in options[]
                  const count = fmt.key === "vocab_matching" && fmtQs.length === 1
                    ? (fmtQs[0].options?.length || 1)
                    : (fmtQs.length || (selectedFormats[fmt.key]?.count ?? fmt.defaultCount));
                  const isLast = idx === arr.length - 1;
                  return (
                    <Box
                      key={fmt.key}
                      sx={{
                        px: 3, py: 2,
                        display: "flex", alignItems: "center", gap: 2,
                        borderBottom: isLast ? "none" : "1px solid",
                        borderBottomColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                        cursor: "pointer",
                      }}
                      onClick={() => setPreviewModal({ formatKey: fmt.key, formatLabel: fmt.label })}
                    >
                      <Icon sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{fmt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmt.key === "writing_composition"
                            ? `${count} prompt${count === 1 ? "" : "s"}`
                            : fmt.key === "vocab_matching"
                            ? `${count} word–definition pairs`
                            : `${count} questions`}
                        </Typography>
                        {(() => {
                          const cfg = getTypeConfig(selectedSubjectKey, fmt.key);
                          const isVocab = fmt.key === "vocab_matching";
                          const isWriting = fmt.key === "writing_composition";
                          const typeMarks = isVocab ? count : isWriting ? (cfg.marksPerQ ?? 30) : (cfg.marksPerQ ?? 1) * count;
                          return (
                            <Chip
                              label={`${typeMarks} marks`}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: "0.7rem", height: 18 }}
                            />
                          );
                        })()}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "primary.main", flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Tap to Preview</Typography>
                        <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
                      </Box>
                    </Box>
                  );
                })}
            </CardContent>
          </Card>

          {/* PDF downloads */}
          {allTestIds.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Download question paper</Typography>
                {allTestIds.map(({ testId, testName, formatLabel }) => (
                  <Box key={testId} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
                      {formatLabel}:
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={
                        downloadKey === `${testId}-question-paper` ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PictureAsPdfIcon />
                        )
                      }
                      disabled={!!downloadKey}
                      onClick={() => handleDownloadPdf(testId, testName, "question-paper", `${testId}-question-paper`)}
                    >
                      Question Paper
                    </Button>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {!saveToDatabase && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Enable "Save to database" in the Configure step to download PDFs.
            </Alert>
          )}

          {/* Save to Database — persistent card when not yet saved */}
          {saveToDatabase && questions.length > 0 && allTestIds.length === 0 && (
            <Card sx={{ mb: 3, borderColor: verifyBatchData?.can_save ? "success.main" : verifyBatchData ? "warning.main" : "divider", borderWidth: 1, borderStyle: "solid" }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Save to Database
                </Typography>
                {verifyBatchData && !verifyBatchData.can_save && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Verification has unresolved issues. Use Tap to Preview on each format, expand Show details on flagged questions, or acknowledge and save anyway.
                    </Typography>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={saveDespiteVerification}
                          onChange={(e) => setSaveDespiteVerification(e.target.checked)}
                          color="warning"
                        />
                      )}
                      label="Save despite verification issues (I have reviewed the risks)"
                    />
                  </>
                )}
                {verifyBatchData?.can_save && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    All questions verified. Ready to save to the database.
                  </Typography>
                )}
                {!verifyBatchData && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {verifyOutcome === "poll_timeout"
                      ? "Verification is still running — it has not failed. Refresh the status above to pick up the result, or save now without it."
                      : verifyOutcome === "failed"
                        ? "Verification failed to run. Questions will be saved without AI verification."
                        : "Verification has not run for these questions. They will be saved without AI verification."}
                  </Typography>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    color={verifyBatchData?.can_save ? "success" : verifyBatchData ? "warning" : "primary"}
                    startIcon={loadingGenerate ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                    disabled={loadingGenerate || (verifyBatchData && !verifyBatchData.can_save && !saveDespiteVerification)}
                    onClick={handleSaveToDatabaseNow}
                  >
                    {loadingGenerate ? "Saving…" : "Save to Database"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {allTestIds.length > 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Test saved successfully! You can download the PDF above or create another test.
            </Alert>
          )}

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button variant="outlined" onClick={() => setActiveStep(1)}>
              Back
            </Button>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={
                  loadingGenerate ? <CircularProgress size={16} /> : <RefreshIcon />
                }
                onClick={handleGenerate}
                disabled={loadingGenerate}
              >
                Generate Again
              </Button>
              <Button variant="contained" onClick={handleStartFresh}>
                Create Another Test
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── Preview Modal ────────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(previewModal)}
        onClose={() => setPreviewModal(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1, flexShrink: 0 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{previewModal?.formatLabel}</Typography>
            <Typography variant="body2" color="text.secondary">
              {previewModal?.formatKey === "writing_composition"
                ? "Edit hints and visibility — changes are saved to the database"
                : "This is how students will see this section"}
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewModal(null)} size="small" sx={{ mt: -0.5, ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent
          sx={{
            pt: 2,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {previewModal && (
            <PreviewModalContent
              formatKey={previewModal.formatKey}
              questions={questions}
              setQuestions={setQuestions}
              verifyBatchId={verifyBatchId}
              onVerificationBatchUpdated={handleVerificationBatchUpdated}
              onQuestionFixed={handleQuestionFixed}
              verificationBusy={verificationBusy}
              setVerificationBusy={setVerificationBusy}
              onError={(msg) => setGenError(msg || "Action failed")}
            />
          )}
        </DialogContent>
      </Dialog>

      <BookViewerDialog
        open={Boolean(bookViewer)}
        onClose={() => setBookViewer(null)}
        title={bookViewer?.title}
        pdfUrl={bookViewer?.pdfUrl}
        epubUrl={bookViewer?.epubUrl}
      />

      <PassageSelectDialog
        open={passageDialogOpen}
        onClose={() => setPassageDialogOpen(false)}
        onSelect={(p) => { setSelectedPassage(p); setPassageDialogOpen(false); }}
      />
    </Box>
  );
};

export default AiAssignmentCreator;
